/**
 * DAG Sync Client
 * 
 * Manages WebSocket connections and DAG synchronization for any context type
 */

import { invalidateReviewAccess } from "../review-access";
import { pendingChangesChanged } from "../pending-changes-cache";
import {
  DAGSubscription,
  DAGContext,
  WebSocketMessage,
  WebSocketResponse,
  SyncDAGResponse,
  DAGPatch,
  DAGNode,
  DAGRelationship,
  type RefusalDetail,
} from './types';
import { PartStreamingClient } from '../part-streaming/client';
import { forgetPinConflicts, setPinConflicts } from '../pins/pin-conflicts';
import { forgetNotedConflicts } from '../pins/noted-conflicts';
import { forgetRetiredPins, setRetiredPins } from '../pins/retired-pins';
import { computedUnavailable, forgetPinnedIsland, pinnedIsland, setPinnedIsland } from '../pins/pinned-islands';
import { activeBranchFor, branchHeaders, getActiveBranch } from '../active-branch';
import { NeoGraphParser } from '@sourcepilot/framework/neograph-utils';
import { graphStore } from '@sourcepilot/framework/graph-store';
import { resolveRelProperty } from '@sourcepilot/framework/reverse-property';
import { RelationshipRegistry } from '@sourcepilot/generated/registries/relationship-registry';
import {
  registerPendingLocalUuid,
  removePendingLocalUuid,
  isPendingLocalUuid,
  setCheckAndApplyCallback
} from '@sourcepilot/framework/local-uuid-registry';
import { ModelRegistry } from '@sourcepilot/generated/registries/model-registry';
import { organisationChanged, socketConnectedSteadily } from './organisation-change';
import { versioningManifest } from '@sourcepilot/generated/api-manifest';
import {
  postOpen, getOpsFile, commitFilePath, postUnversioned, OpenFailed,
  genesisRetryMs, refusalDetail, refusalText, GENESIS_BUDGET_MS, NOT_AVAILABLE,
  type OpenRefusal, type UnversionedRow, type OpenAnswer, type OpenIsland, type OpsFile,
} from './serving-open';
import { loadStateFromPacks } from '../state-packs/loader';
import { packCacheFor, type PackCache } from '../state-packs/pack-cache';
import { PackPrefetcher } from '../state-packs/prefetch';
import { ComputedChannel } from '../enrichment/computed-channel';
import { getServingIdentity, isServingIdentityByNodeId, isServingImpersonating, onServingIdentityChange } from './serving-identity';
import {
  entryMatches, isDraftLane, islandStoreFor, noIslandStore, sessionEnded, storableLane, userOf, withinCatchUp,
  CLIENT_BUILD, DEFAULT_LANE, DRAW_BEFORE_OPEN, STORE_FORMAT, STORE_MAX_CATCHUP_OPS,
  type HeldIsland, type IslandStore, type StoredBase, type StoredPatch,
} from '../island-store/island-store';
import { markAnswered, markDrawn, markOffline } from '../island-store/unconfirmed';
import { ClaimsChannel } from '../enrichment/claims-channel';
import { noteCommitProgress, noteProgressSocket } from '../commit-progress';

export type DAGSyncEventHandler = (subscription: DAGSubscription) => void;
export type ErrorEventHandler = (error: string) => void;

// Re-export for backward compatibility - these are now imported from local-uuid-registry
export { registerPendingLocalUuid, removePendingLocalUuid, isPendingLocalUuid };

// ============================================================================
// LocalUUID Optimistic UI - Module-level State
// ============================================================================
//
// Clean Architecture for Optimistic UI:
// - pendingLocalUuids: Tracked in local-uuid-registry.ts (breaks circular dep)
// - contextsWithBufferedPatches: Set of context IDs currently buffering
// - bufferedPatchesPerContext: Ordered list of ALL buffered patches per context
//
// CRITICAL: Once a context has pending local UUIDs, ALL subsequent patches
// for that context must be buffered to preserve ordering!
//
// Flow:
// 1. On add() with new item → generate localUuid → register pending (with context)
// 2. On DAG patch:
//    - If context has ANY pending local UUIDs → buffer the patch
//    - If no pending → apply normally
// 3. On modify/create response:
//    - Remove local UUID from pending
//    - If context has no more pending → apply ALL buffered patches in order
// ============================================================================

// Set of contexts that have buffered patches (detected when patch with pending _localUuid arrives)
const contextsWithBufferedPatches = new Set<string>();

// Map: contextId → ordered list of buffered DAG patches
// ALL patches for a context with pending items are buffered to preserve order
const bufferedPatchesPerContext = new Map<string, DAGPatch[]>();

/**
 * Check if a context has buffered patches (and should continue buffering)
 */
export function contextHasPendingLocalUuids(contextId: string): boolean {
  return contextsWithBufferedPatches.has(contextId);
}

/**
 * Mark a context as having buffered patches
 */
function markContextAsBuffering(contextId: string): void {
  contextsWithBufferedPatches.add(contextId);
}

/**
 * Check if all pending local UUIDs for a context's buffered patches are resolved
 * If yes, apply all buffered patches and clear the buffer
 */
function checkAndApplyBufferedPatches(contextId: string): void {
  if (!contextsWithBufferedPatches.has(contextId)) {
    return;  // No buffered patches for this context
  }

  const patches = bufferedPatchesPerContext.get(contextId) || [];

  // Scan all buffered patches to see if any _localUuid is still pending
  for (const patch of patches) {
    for (const nodeChange of patch.changes.nodes) {
      const localUuid = nodeChange.properties?._localUuid;
      if (localUuid && isPendingLocalUuid(localUuid)) {
        // Still have pending items - don't apply yet
        console.log(`📦 [LocalUUID] Context ${contextId} still has pending: ${localUuid.substring(0, 20)}...`);
        return;
      }
    }
  }

  // All pending items resolved - apply buffered patches
  console.log(`✓ [LocalUUID] All pending resolved for ${contextId} - applying ${patches.length} buffered patches`);
  applyAllBufferedPatches(contextId);

  // Clear the buffering state
  contextsWithBufferedPatches.delete(contextId);
}

/**
 * Check ALL contexts with buffered patches to see if any are now resolved
 * Called from promoteToGraphStore after removing pending local UUIDs
 */
function checkAndApplyBufferedPatchesForAllContexts(): void {
  // Make a copy since we might modify during iteration
  const contexts = Array.from(contextsWithBufferedPatches);

  for (const contextId of contexts) {
    checkAndApplyBufferedPatches(contextId);
  }
}

/**
 * Buffer a DAG patch for a context
 */
function bufferDAGPatch(contextId: string, patch: DAGPatch): void {
  if (!bufferedPatchesPerContext.has(contextId)) {
    bufferedPatchesPerContext.set(contextId, []);
  }
  bufferedPatchesPerContext.get(contextId)!.push(patch);
  console.log(`📦 [LocalUUID] Buffered patch for context ${contextId} (total buffered: ${bufferedPatchesPerContext.get(contextId)!.length})`);
}

/**
 * Get all buffered patches for a context
 */
export function getBufferedPatches(contextId: string): DAGPatch[] {
  return bufferedPatchesPerContext.get(contextId) || [];
}

/**
 * Clear all buffered patches for a context
 */
export function clearBufferedPatches(contextId: string): void {
  bufferedPatchesPerContext.delete(contextId);
}

// Reference to the current DAGSyncClient instance (for applying buffered patches)
let currentDAGSyncClient: DAGSyncClient | null = null;

/**
 * Register the DAGSyncClient instance for LocalUUID operations
 * Called when the client is created
 */
export function setCurrentDAGSyncClient(client: DAGSyncClient | null): void {
  currentDAGSyncClient = client;

  // Register the callback so local-uuid-registry can trigger patch application
  // This breaks the circular dependency: neo-node-base -> local-uuid-registry (no cycle)
  // instead of: neo-node-base -> dag-sync/client (would create cycle)
  setCheckAndApplyCallback(checkAndApplyBufferedPatchesForAllContexts);
}

/**
 * Apply all buffered patches for a context in order
 * Called when all pending local UUIDs for the context are resolved
 *
 * @param contextId - The context to apply patches for
 */
export function applyAllBufferedPatches(contextId: string): void {
  const patches = bufferedPatchesPerContext.get(contextId);
  if (!patches || patches.length === 0) {
    return;
  }

  if (!currentDAGSyncClient) {
    console.error(`❌ [LocalUUID] Cannot apply buffered patches - no DAGSyncClient registered`);
    return;
  }

  for (const patch of patches) {
    currentDAGSyncClient.applyBufferedPatch(patch);
  }

  // Clear the buffer
  bufferedPatchesPerContext.delete(contextId);
}

/** Phase C (67 §9c; THE REBASE RULE 67 §5e): may a subscription on
 *  `subBranch` apply a patch tagged `patchBranch`? Mirrors the server's
 *  delivery filter (defence in depth — the server already drops what a
 *  subscriber cannot see): untagged = broadcast; same branch = yes; an
 *  OVERLAY (wip/, proposal/) follows its base so it also takes the
 *  base's patches (main — proposal targets resolve server-side); an
 *  explicit branch is frozen and sees only its own. */
export function branchVisible(patchBranch: string | undefined, subBranch: string | undefined): boolean {
  if (!patchBranch) return true;
  const sub = subBranch || 'main';
  if (patchBranch === sub) return true;
  const overlay = /^(wip|proposal)\//.test(sub);
  return overlay && patchBranch === 'main';
}

/** A stored head's base, rebuilt from the pack cache. */
type BaseRows = { root_nodes: Record<string, unknown>[]; nodes: Record<string, unknown>[]; relationships: Record<string, unknown>[] };

/** One island of a serving open answer, and the subscription it loads. */
type ServingTarget = {
  subscription: DAGSubscription;
  entry: { gen: string | null; fs: string; lane: string; subscribed: boolean; computed?: ComputedChannel; claims?: ClaimsChannel; computedSince?: string; claimsSince?: string; groupOf?: string; openedBy?: string[]; unversionedIds?: string[]; supersededGen?: string; supersededTries?: number; at?: string; line?: string; epoch?: string | null; drawn?: { held: HeldIsland; added: Set<string> }; read?: HeldIsland };
  island?: OpenIsland;
};

/** Which records in a subscription's state are the ISLAND'S OWN content,
 *  the ones a re-load may remove: the root, and records of the island's
 *  member types (`versioningManifest`) — never a referent (User,
 *  Workspace), another island's root or a record of a class the island does
 *  not hold. A row the server stamps with its island (`_island`) is the
 *  island's only when that is this island. A context that is not an island
 *  has no own content: nothing is removed, as before. */
function ownContent(sub: DAGSubscription): (node: any) => boolean {
  const key = versioningManifest.islands ? versioningManifest.island_of_type[sub.context_type] : undefined;
  const members = new Set(key ? versioningManifest.islands[key]?.member_types ?? [] : []);
  return (node: any) => {
    if (!key || !node) return false;
    if (node.id === sub.context_id) return true;
    // stamped (contract v1.1: every row of the island's VERSIONED content;
    // absent = a referent, an owned satellite or a claim, none of them removed)
    if (typeof node._island === 'string') return node._island === sub.context_id;
    return members.has(node._label);
  };
}

/** Compare two copies of a record's computed fields: by `_v` (the item
 *  version, wire contract §4: the writing transaction's ms on Neo4j's clock)
 *  when both carry one, else by `evaluated_at`. 1 = `a` is newer. */
function compareComputed(a: Record<string, unknown>, b: Record<string, unknown>): number {
  if (typeof a._v === "number" && typeof b._v === "number") return a._v === b._v ? 0 : a._v > b._v ? 1 : -1;
  return compareVersions(a.evaluated_at, b.evaluated_at);
}

/** Compare two versions of a computed value (`evaluated_at`: an ISO time
 *  or ms): 1 when `a` is newer, -1 when older, 0 when equal or either is
 *  unknown. */
function compareVersions(a: unknown, b: unknown): number {
  const t = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' ? Date.parse(v) : NaN);
  const ta = t(a), tb = t(b);
  if (Number.isNaN(ta) || Number.isNaN(tb) || ta === tb) return 0;
  return ta > tb ? 1 : -1;
}

/** An op's metadata as a frame carries it (VC-D's wire, serving brief §4a). */
type WireOp = {
  op_id?: string;
  ts?: number;
  depends_on?: string[];
  depends_on_ts?: Record<string, number>;
};

function opsOf(patch: DAGPatch): WireOp[] {
  const ops = (patch as unknown as { ops?: unknown }).ops;
  return Array.isArray(ops) ? (ops as WireOp[]) : [];
}

/** The commit an open answers an island's base at: its manifest's, else the
 *  one its manifest URL names (`…/state/<island>/<line>/<commit>/manifest.json`);
 *  null when it has no base (an island born in the log). */
function openBaseCommit(island: OpenIsland): string | null | undefined {
  if (island.state.manifest) return island.state.manifest.commit;
  if (!island.state.base) return null;
  const m = /\/([^/?]+)\/manifest\.json(?:\?|$)/.exec(island.state.base);
  return m ? decodeURIComponent(m[1]) : undefined;
}

/** An edge whose source or target is not in the graph store yet. */
type HeldEdge = {
  subscriptionId: string;
  source: string;
  target: string;
  type: string;
  properties?: Record<string, any>;
};

/** A record-announce (67 §9c third lane): a satellite record was written;
 *  the record is durable and queryable — consumers refetch. */
export type RecordAnnounceEvent = {
  context_id: string;
  node_id: string;
  node_type: string;
  /** legacy; empty for routed announces */
  pinned_commit_id?: string;
  /** "created" | "updated" */
  change?: string;
  /** logical id of the entity the record is ABOUT */
  attached_entity?: string;
  /** content hash the record's claim was computed against, if state-referential */
  attached_hash?: string;
};

export class DAGSyncClient {
  private websocket: WebSocket | null = null;
  private subscriptions = new Map<string, DAGSubscription>();
  private eventHandlers = new Map<string, Set<DAGSyncEventHandler>>(); // Multiple handlers per subscription
  private errorHandlers = new Set<ErrorEventHandler>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  /** cancels the 4005 count's reset when the socket closes first */
  private steadyConnection: (() => void) | null = null;
  private isConnecting = false;

  // Buffer messages that arrive before handlers are registered (to prevent race conditions)
  private pendingUpdates = new Map<string, DAGSubscription[]>();

  // Reference counting for subscriptions (to prevent premature unsubscribes)
  private subscriptionRefCounts = new Map<string, number>();

  // Pending unsync operations (debounced to handle navigation race conditions)
  private pendingUnsyncs = new Map<string, NodeJS.Timeout>();

  // Part streaming client uses the same WebSocket
  public partStreamingClient: PartStreamingClient | null = null;

  // --- Versioning op-stream consumption (masterplan 67 §9, item 5) ---
  // Seq catch-up cursors per subscription: an op-stream position
  // from GET /versioning/{island}/ops/since, seeded by the serving open;
  // on reconnect, replay everything since the stored cursor through the
  // normal patch path (idempotent, so overlap with what the open already
  // applied is safe). Islands only — non-island contexts 404 and are skipped.
  private catchupCursors = new Map<string, number>();
  // record-announce consumers (67 §9 third lane): `_record_announce`-marked
  // creations notify these instead of entering the graph store (the record
  // is durable + queryable — consumers refetch).
  // OP-METADATA subscribers (67 §9; 2026-08-28): the versioning relay
  // attaches the serialised ops to each dag_patch — version UIs consume
  // them directly over the SAME socket (no polling, one format).
  private opsHandlers = new Set<
    (e: { context_type: string; context_id: string; ops: unknown[]; branch?: string }) => void
  >();
  // subscriptions created purely to FOLLOW OPS (no state hydration wanted)
  private opsOnlySubs = new Set<string>();

  // Announce payload (67 §9c): the attachment COORDINATE rides along —
  // consumers hash-match `attached_hash` against their lens head (relevance
  // is by hash, not by branch). `pinned_commit_id` is legacy (always empty
  // for routed announces; the pin lives in the registry row).
  private recordAnnounceHandlers = new Set<(a: RecordAnnounceEvent) => void>();

  // EDGES HELD FOR A MISSING END (`setEdge`): by edge key, and the keys
  // waiting on each missing node id. Released when the node lands in the
  // graph store, dropped with their subscription.
  private heldEdges = new Map<string, HeldEdge>();
  // LOAD BUFFERS (VC-UI fix 3): the live patches that arrive between a
  // subscribe and the end of its load (state + catch-up), per subscription.
  // Applied after the load, minus what the load already reflects.
  private loadBuffers = new Map<string, DAGPatch[]>();
  // EARLY ENRICHMENT (VC-UI fix 4): computed values (a `derived` patch) for
  // a record this client does not hold yet, by record id. Laid over the
  // record when it lands, from any load; dropped with their subscription.
  private earlyEnrichment = new Map<string, { subscriptionId: string; fields: Record<string, any> }>();
  // OP ORDER (the ops fixes' client half, serving brief §4a; wire per VC-D):
  // the ops each subscription has applied (op_id → ts) since its cursor —
  // to drop duplicates and to see a missing dependency; the frames HELD
  // because one of their dependencies has not arrived; the catch-up in
  // flight to fetch it; and the quiet-interval catch-up timer.
  private seenOps = new Map<string, Map<string, number>>();
  private gapHeld = new Map<string, DAGPatch[]>();
  private gapCatchUps = new Map<string, Promise<void>>();
  private quietTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** when each subscription's last catch-up answered (ms): the parity dump
   *  reports it, so a change missed by a client that is only polling can be
   *  told apart as made after its last poll */
  private lastCatchUpAt = new Map<string, number>();
  /** The last ops this client SKIPPED as already reflected, and why (the
   *  parity dump reports them): when a change never reaches the store, this
   *  says whether a rule here judged it covered — e.g. stamped at or before a
   *  cursor that was taken after the state it came with. */
  private droppedOps: { subscriptionId: string; op_id?: string; ts?: number; cursor?: number; reason: string; at: number }[] = [];
  /** The last moves of each subscription's cursor and what made each (the
   *  parity dump reports them), so a skipped op can be matched to the move
   *  that put the cursor past it. */
  private cursorMoves: { subscriptionId: string; cursor: number; source: string; at: number; frameOps?: string[] }[] = [];
  private moveCursor(subscriptionId: string, cursor: number, source: string, frameOps?: string[]): void {
    this.catchupCursors.set(subscriptionId, cursor);
    this.cursorMoves.push({ subscriptionId, cursor, source, at: Date.now(), ...(frameOps ? { frameOps } : {}) });
    if (this.cursorMoves.length > 200) this.cursorMoves.shift();
  }
  private noteDropped(subscriptionId: string, op: WireOp, cursor: number | undefined, reason: string): void {
    this.droppedOps.push({ subscriptionId, op_id: op.op_id, ts: op.ts, cursor, reason, at: Date.now() });
    if (this.droppedOps.length > 200) this.droppedOps.shift();
  }
  /** A load that fails after its open is tried again after these pauses,
   *  then the subscription errs 503. */
  static OPEN_RETRY_MS = [1_000, 3_000, 10_000];
  /** A draft's claims are caught up this long after its last own edit. */
  static CLAIMS_AFTER_EDIT_MS = 1_000;
  /** Catch up an island after this long with no frame (± 20%). */
  static QUIET_MS = 45_000;
  /** At most this many op ids remembered per subscription. */
  static SEEN_CAP = 5_000;
  private edgesWaitingOn = new Map<string, Set<string>>();
  // A RECONNECT (VC-UI fix 9): the app opens a NEW socket, and with it a new
  // client (getDAGSyncClient), whose subscriptions start empty. Its first
  // load of each subscription compares the new state with what the PREVIOUS
  // client held for it (`priorState`) — compared with nothing, a re-load
  // removed nothing, and a record deleted while the socket was down stayed
  // on screen. The previous client is disposed: its timers, its graph-store
  // listener and any catch-up still in flight stop touching the store.
  private priorState = new Map<string, { nodes: Map<string, DAGNode>; relationships: Map<string, DAGRelationship> }>();
  private offNodeCreated: () => void = () => {};
  private disposed = false;
  /** Each subscription loaded through the serving open: the draft generation
   *  its socket subscription is bound to, the field-set fingerprint `fs`, the
   *  lane, its computed channel, and a re-open in progress (or asked for
   *  while one ran). */
  private serving = new Map<string, {
    gen: string | null; fs: string; lane: string; subscribed: boolean;
    computed?: ComputedChannel; claims?: ClaimsChannel; computedSince?: string; claimsSince?: string;
    retryTimer?: ReturnType<typeof setTimeout>; claimsTimer?: ReturnType<typeof setTimeout>;
    genesisSince?: number; opening?: boolean; again?: boolean; fsSeen?: string;
    /** this load already re-opened once on an op file's 410 */
    reopenedOn410?: boolean;
    /** the gen a `dag-gen-superseded` last named, and when: a re-open that
     *  answers it again (the open and the socket disagree on the current
     *  gen) is retried with backoff instead of at every frame */
    supersededGen?: string;
    supersededAt?: number;
    supersededTries?: number;
    supersededTimer?: ReturnType<typeof setTimeout>;
    /** the socket has acknowledged this subscription's (re)subscribe; false
     *  between sending it and its answer */
    acked?: boolean;
    /** the load caught up before the subscribe was acknowledged: an op
     *  published in between reached neither (catch up once more on the ack) */
    caughtUpUnacked?: boolean;
    /** an island this open expands was refused genesis_pending: the group is
     *  re-opened after its retry_after_ms, within GENESIS_BUDGET_MS */
    expandedRetryTimer?: ReturnType<typeof setTimeout>;
    expandedGenesisSince?: number;
    /** loads that failed after the open, in a row (OPEN_RETRY_MS) */
    failures?: number;
    /** PINNED (V17; VC-D's pinned open): the open answered this island AT a
     *  holder's pin — that commit only (lane null, no tail). It is kept
     *  fixed: never subscribed, caught up or fed a frame. It moves only when
     *  the holder's pin does: the opener is opened again. */
    at?: string;
    /** an expanded island: the subscription whose open added it, and the
     *  nested load spec it was opened with (passed back on its re-open) */
    groupOf?: string;
    spec?: unknown;
    /** BOOKKEEPING for the parity dump only (VC-e2e's compare, b2i): why
     *  the open answered this island, and the unversioned rows its load
     *  read. Nothing reads them to decide what the store holds. */
    openedBy?: string[];
    unversionedIds?: string[];
    /** the open's line and segment epoch (what a stored entry is checked
     *  against) */
    line?: string;
    epoch?: string | null;
    /** DRAWN BEFORE THE OPEN ANSWERED (stage 2): the stored head and list
     *  the page shows, and the records the draw added to the graph store
     *  (what a refusal clears) */
    drawn?: { held: HeldIsland; added: Set<string> };
    /** the entry the draw read, drawn or not (the open may answer first):
     *  the confirm uses it rather than reading it again */
    read?: HeldIsland;
  }>();
  /** EXPANDED ISLANDS: who holds each one, and what each opener holds */
  private memberOf = new Map<string, Set<string>>();
  private membersOf = new Map<string, Set<string>>();
  /** unversioned rows held (§5), by id: the newest `_v` applied */
  private unversioned = new Map<string, number>();
  /** packs kept between loads by name (immutable); B4 makes it persistent */
  /** memory, over the persistent store when the session names its
   *  organisation (B4); chosen anew whenever who the data is read as changes */
  packCache: PackCache = packCacheFor(getServingIdentity());
  /** DRAFT SEGMENTS HELD, per (island, lane): the gen they belong to, the
   *  `to` of the newest, and their files. A draft's segments only grow
   *  within a gen and never change, so a re-open on the same gen asks for
   *  the ops after `to` alone (`last_segment`) and re-applies these first —
   *  the re-load starts from the base, which has none of them (VC-D, wire
   *  v1.9e). Kept across unsubscribes for this client; the oldest go first. */
  /** Keyed by (identity, island, lane); each entry knows the gen, the
   *  segment epoch and the field set its files were rendered under. A
   *  different gen, epoch or field set starts over — a seal moves the epoch
   *  but not the gen, and segments from before it are in the new base, so
   *  laid over later commits they would put old values back — and an
   *  identity change (impersonation, an organisation switch) drops them all. */
  private heldSegments = new Map<string, { gen: string; epoch?: string; fs: string; to: number; files: OpsFile[] }>();
  static HELD_SEGMENTS_MAX = 64;
  /** THE ISLAND STORE (stage 2, shape B; island-store.ts): each island's
   *  head (its load's base packs and a cursor) and the list of patches
   *  applied since, per (user, organisation), used by the next page's load
   *  when the open answers the same coordinate; chosen anew whenever who
   *  the data is read as changes */
  islandStore: IslandStore = islandStoreFor(getServingIdentity(), { impersonating: isServingImpersonating(), byNodeId: isServingIdentityByNodeId() });
  /** THE LIST BEING RECORDED, per subscription: the patches applied since
   *  the last write, and where they go in the stored list. Started by a load
   *  (a cold load's base, or a stored island's list); none when the lane is
   *  not kept or the list was dropped (until the next cold load). */
  private storeLog = new Map<string, { patches: StoredPatch[]; seq: number }>();
  /** a cold load's base, until its head is written */
  private coldBase = new Map<string, StoredBase>();
  /** each island's writes, one after another (a head before its appends) */
  private storeWrites = new Map<string, Promise<void>>();
  private storeTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** an island's state is written this long after its last change */
  static STORE_WRITE_MS = 5_000;
  private offPageHide: () => void = () => {};
  /** PACK PREFETCH (client only, Tom 2026-10-19): a viewed line's new
   *  commit's packs into the pack cache in idle time (state-packs/prefetch) */
  prefetcher = new PackPrefetcher({ base: () => this.restBase(), cache: () => this.packCache });
  private offIdentity = onServingIdentityChange((identity) => {
    this.prefetcher.clear();
    this.heldSegments.clear();
    this.answeredLane.clear();
    this.packCache.clear?.();
    this.packCache = packCacheFor(identity);
    for (const t of Array.from(this.storeTimers.values())) clearTimeout(t);
    this.storeTimers.clear();
    // what was recorded was read as the previous identity
    this.storeLog.clear();
    this.coldBase.clear();
    this.islandStore = islandStoreFor(identity, { impersonating: isServingImpersonating(), byNodeId: isServingIdentityByNodeId() });
  });
  private heldKey(island: string, lane: string | undefined): string {
    return `${getServingIdentity() ?? ''}|${island}|${lane ?? 'main'}`;
  }
  /** A subscription's stale-claims choice, for the open request. */
  private claimsChoice(sub: DAGSubscription | undefined): { claims?: { stale: 'send' | 'omit' } } {
    return sub?.claimsStale ? { claims: { stale: sub.claimsStale } } : {};
  }

  /** the lane an open ANSWERED for the lane a page asked (`main` → my draft
   *  `wip/<me>`): a new subscription asks for `main` and only learns its
   *  draft from the answer, so the held segments (kept by the answered
   *  lane) are found through this. Same key and same clearing as them. */
  private answeredLane = new Map<string, string>();
  private lastSegmentFor(island: string, lane: string | undefined): { last_segment?: { gen: string; epoch?: string; to: number } } {
    const asked = this.heldKey(island, lane);
    const answered = this.answeredLane.get(asked);
    const held = this.heldSegments.get(asked) ?? (answered !== undefined ? this.heldSegments.get(this.heldKey(island, answered)) : undefined);
    return held ? { last_segment: { gen: held.gen, ...(held.epoch !== undefined ? { epoch: held.epoch } : {}), to: held.to } } : {};
  }

  constructor(private websocketUrl: string, existingSocket?: WebSocket) {
    // Register this client for LocalUUID operations
    setCurrentDAGSyncClient(this);
    this.offNodeCreated = graphStore.onNodeCreated((node) => {
      this.applyEarlyEnrichment(node);
      this.releaseEdgesWaitingOn((node as any).id);
    });
    // a page leaving writes what it has not written yet
    if (typeof window !== 'undefined') {
      const onHide = () => this.storeAll();
      window.addEventListener('pagehide', onHide);
      this.offPageHide = () => window.removeEventListener('pagehide', onHide);
    }

    if (existingSocket) {
      this.websocket = existingSocket;
      this.setupExistingSocket();
    }
  }

  /**
   * Apply a buffered DAG patch (public method for LocalUUID system)
   * Called when all pending local UUIDs for a context are resolved
   */
  applyBufferedPatch(patch: DAGPatch): void {
    const subscriptionId = `${patch.context_type}:${patch.context_id}`;
    const subscription = this.subscriptions.get(subscriptionId);

    if (!subscription) {
      console.warn(`Cannot apply buffered patch - subscription not found: ${subscriptionId}`);
      return;
    }

    this.applyDAGPatchToGraphStore(patch, subscription);
  }

  // Set up message handling for existing WebSocket
  private setupExistingSocket(): void {
    if (!this.websocket) return;

    // Initialize part streaming client with the existing WebSocket
    this.partStreamingClient = new PartStreamingClient(this.websocket);

    // Don't override existing handlers, just add our message handling
    const originalOnMessage = this.websocket.onmessage;
    this.websocket.onmessage = (event) => {
      // Call original handler first
      if (originalOnMessage) {
        originalOnMessage.call(this.websocket as WebSocket, event);
      }
      // Then our handler
      this.handleMessage(event);
    };
  }

  // Wait for WebSocket to be ready
  private waitForConnection(): Promise<void> {
    if (!this.websocket) {
      return Promise.reject(new Error('No WebSocket connection'));
    }

    if (this.websocket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 5000);

      const onOpen = () => {
        clearTimeout(timeout);
        this.websocket!.removeEventListener('open', onOpen);
        this.websocket!.removeEventListener('error', onError);
        resolve();
      };

      const onError = () => {
        clearTimeout(timeout);
        if (this.websocket) {
          this.websocket.removeEventListener('open', onOpen);
          this.websocket.removeEventListener('error', onError);
        }
        reject(new Error('WebSocket connection failed'));
      };

      if (this.websocket) {
        this.websocket.addEventListener('open', onOpen);
        this.websocket.addEventListener('error', onError);
      }
    });
  }

  // Connection Management
  
  async connect(): Promise<void> {
    if (this.websocket?.readyState === WebSocket.OPEN || this.isConnecting) {
      return; // Already connected or connecting
    }

    // If we don't have a websocket URL, we're using an existing socket
    if (!this.websocketUrl) {
      return Promise.resolve();
    }

    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      try {
        this.websocket = new WebSocket(this.websocketUrl);

        this.websocket.onopen = () => {
          // WebSocket connected
          this.isConnecting = false;
          this.steadyConnection = socketConnectedSteadily();
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Initialize part streaming client with the same WebSocket
          this.partStreamingClient = new PartStreamingClient(this.websocket!);
          // a commit's silence counts from here (item 258)
          noteProgressSocket(true);
          
          resolve();
        };

        this.websocket.onclose = (event) => {
          // WebSocket disconnected
          this.isConnecting = false;
          this.steadyConnection?.();
          this.steadyConnection = null;
          noteProgressSocket(false);
          // 4005: the session acts as another organisation now — the page
          // reloads (or, reloading again and again, stops with a prompt);
          // nothing is re-sent on this client
          if (organisationChanged(event)) return;
          this.handleDisconnection();
        };

        this.websocket.onerror = (error) => {
          console.error('DAG sync WebSocket error:', error);
          this.isConnecting = false;
          this.notifyErrorHandlers(`WebSocket connection error: ${error}`);
          reject(error);
        };

        this.websocket.onmessage = (event) => {
          this.handleMessage(event);
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private async handleDisconnection(): Promise<void> {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      // Attempting to reconnect
      
      // Mark all subscriptions as loading
      this.subscriptions.forEach(sub => {
        sub.isLoading = true;
        this.notifySubscriptionUpdate(sub);
      });

      setTimeout(() => {
        this.connect().then(() => {
          // Resubscribe to all contexts
          this.resubscribeAll();
        }).catch(() => {
          this.reconnectDelay *= 2; // Exponential backoff
        });
      }, this.reconnectDelay);
    } else {
      this.notifyErrorHandlers('Failed to reconnect after maximum attempts');
    }
  }

  /** URL MODE ONLY (a client that opened its own socket, `connect()`): after
   *  a reconnect, catch every live subscription up. GAP — know it before
   *  relying on it: `syncDAG` REUSES a live subscription, so this neither
   *  re-opens the island (the serving open) nor re-sends `sync-dag` on the
   *  new socket; only the op catch-up runs, and the server-side follow is
   *  not restored. Nothing in the app uses URL mode today: every app caller
   *  of `getDAGSyncClient` waits for the shared socket (Shell's), and a
   *  reconnect there builds a NEW client that `inheritFrom`s the old one and
   *  opens afresh. URL mode is the tests' and the scripts'. Give it a real
   *  re-open (as the shared path does) before any page depends on it. */
  private async resubscribeAll(): Promise<void> {
    const { ModelRegistry } = await import('@sourcepilot/generated/registries/model-registry');

    const resubscribePromises = Array.from(this.subscriptions.values()).map(sub => {
      // a pinned island is one commit: nothing to resubscribe or catch up
      if (this.serving.get(sub.subscription_id)?.at) return Promise.resolve();
      const ModelClass = ModelRegistry[sub.context_type];
      if (!ModelClass) {
        console.error(`Cannot resubscribe: Model class not found for ${sub.context_type}`);
        return Promise.resolve();
      }

      return this.syncDAG({
        context: ModelClass,
        context_id: sub.context_id
      }, sub.subscription_id).then(() =>
        // seq catch-up (67 §9): replay anything the outage missed through
        // the op lane; idempotent alongside what the open applied
        this.catchUp(sub.context_id, sub.subscription_id)
      );
    });

    await Promise.all(resubscribePromises);
  }

  disconnect(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
  }

  // DAG Synchronization

  async syncDAG(
    context: DAGContext,
    subscriptionId?: string,
  ): Promise<DAGSubscription> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      await this.connect();
    }

    // Wait for connection to be ready if it's still connecting
    if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
      await this.waitForConnection();
    }

    const subId = subscriptionId || this.generateSubscriptionId(context);


    // Extract label from class constructor
    const contextType = context.context.label;

    // Cancel any pending unsync for this subscription (navigation race condition)
    const pendingUnsync = this.pendingUnsyncs.get(subId);
    if (pendingUnsync) {
      clearTimeout(pendingUnsync);
      this.pendingUnsyncs.delete(subId);
      // Cancelled pending unsync
    }

    // Increment reference count for this subscription
    const currentRefCount = this.subscriptionRefCounts.get(subId) || 0;
    this.subscriptionRefCounts.set(subId, currentRefCount + 1);

    // If already subscribed (ref count was > 0), return existing subscription
    // Don't send duplicate sync-dag message. An ERRORED subscription is
    // never reused — the caller is retrying (e.g. a 404 creation race) and
    // needs the sync-dag actually re-sent.
    // An OPS-ONLY follow (followOps) of the same context is never reused as
    // a full subscription: its socket answer does not hydrate the store. A
    // full sync UPGRADES it — the island is opened (the serving open), and
    // the open hydrates.
    const upgradingOpsOnly = this.opsOnlySubs.has(subId);
    if (
      currentRefCount > 0 &&
      this.subscriptions.has(subId) &&
      !this.subscriptions.get(subId)!.error &&
      !upgradingOpsOnly
    ) {
      const existingSub = this.subscriptions.get(subId)!;
      // Reusing existing subscription

      // Immediately notify all event handlers with the current subscription state
      // This ensures the new component gets the existing data
      const handlers = this.eventHandlers.get(subId);
      if (handlers && handlers.size > 0) {
        // Schedule notification in next tick to avoid calling handler during sync
        setTimeout(() => {
          handlers.forEach(handler => handler(existingSub));
        }, 0);
      } else {
        // If no handlers yet, buffer the update for when handlers are registered
        if (!this.pendingUpdates.has(subId)) {
          this.pendingUpdates.set(subId, []);
        }
        this.pendingUpdates.get(subId)!.push(existingSub);
      }

      return existingSub;
    }

    // Phase C: the island's bound branch rides the subscription
    const branch = activeBranchFor(context.context_id);
    if (upgradingOpsOnly) this.opsOnlySubs.delete(subId);

    // Create or update subscription
    if (!this.subscriptions.has(subId)) {
      this.subscriptions.set(subId, {
        subscription_id: subId,
        context_type: contextType,
        context_id: context.context_id,
        branch,
        ...(context.claims ? { claimsStale: context.claims.stale } : {}),
        nodes: new Map(),
        relationships: new Map(),
        version: 0,
        timestamp: new Date().toISOString(),
        isLoading: true
      });
    }

    const subscription = this.subscriptions.get(subId)!;
    if (context.claims) subscription.claimsStale = context.claims.stale;
    subscription.isLoading = true;
    subscription.error = undefined;
    subscription.errorCode = undefined;
    subscription.errorDetail = undefined;
    subscription.branch = branch;
    this.notifySubscriptionUpdate(subscription);

    // frames held for a gap go into the load's buffer: the load and its
    // catch-up bring what they waited for, and the flush drops duplicates
    this.loadBuffers.set(subId, this.gapHeld.get(subId) ?? []);
    this.gapHeld.delete(subId);
    // THE SERVING OPEN loads every subscription (option B): the open, then
    // the socket subscribe naming the draft's gen (`openServing`)
    void this.openServing(subscription);
    return subscription;
  }

  async unsyncDAG(context: DAGContext): Promise<void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      return;
    }

    const subId = this.generateSubscriptionId(context);
    const contextType = context.context.label;

    // Decrement reference count
    const currentRefCount = this.subscriptionRefCounts.get(subId) || 0;
    if (currentRefCount <= 0) {
      console.warn(`[DAG Sync] Attempted to unsync ${contextType}:${context.context_id} but refCount is already 0`);
      return;
    }

    const newRefCount = currentRefCount - 1;
    this.subscriptionRefCounts.set(subId, newRefCount);

    // Only actually unsubscribe if no more components are using this subscription
    if (newRefCount === 0) this.scheduleFinalRelease(subId, contextType, context.context_id);
  }

  /** The last hold on a subscription went — a page view's (unsyncDAG) or an
   *  ops follow's on a subscription a view had made full: release it after
   *  the debounce, unless a new hold arrives first. */
  private scheduleFinalRelease(subId: string, contextType: string, contextId: string): void {
    const previous = this.pendingUnsyncs.get(subId);
    if (previous) clearTimeout(previous);
    // Debounce the actual unsync to handle navigation race conditions
    // If we're navigating to another page that uses the same thread,
    // the new component will mount and increment the refCount before this timeout fires
    const timeout = setTimeout(() => {
      // Double-check refCount hasn't been incremented during the debounce period
      const finalRefCount = this.subscriptionRefCounts.get(subId) || 0;
      if (finalRefCount === 0 && this.memberOf.get(subId)?.size) {
        // its page left, but a live opener still holds it as a member (a
        // project's library item, visited on its own page and left): it
        // stays, as that opener's
        this.subscriptionRefCounts.delete(subId);
        this.pendingUnsyncs.delete(subId);
      } else if (finalRefCount === 0) {
        // Sending unsync-dag
        const message: WebSocketMessage = {
          type: "unsync-dag",
          context_type: contextType,
          context_id: contextId
        };

        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify(message));
        }

        // its own rows leave the store (unless another view holds them),
        // then the subscription and its handlers, and its pin alerts
        this.releaseOwnRows(this.subscriptions.get(subId));
        forgetPinConflicts(contextId);
        forgetNotedConflicts(contextId);
        forgetRetiredPins(contextId);
        if (this.serving.get(subId)?.at) forgetPinnedIsland(contextId);
        this.subscriptions.delete(subId);
        this.forgetHeldFor(subId);
        this.loadBuffers.delete(subId);
        this.releaseMembers(subId);
        this.eventHandlers.delete(subId);
        this.subscriptionRefCounts.delete(subId);
        this.pendingUnsyncs.delete(subId);
      } else {
        // Cancelled unsync - refCount increased during debounce
        this.pendingUnsyncs.delete(subId);
      }
    }, 100); // 100ms debounce to handle navigation transitions

    this.pendingUnsyncs.set(subId, timeout);
  }

  // Part streaming is now handled by the separate PartStreamingClient
  // Access via: client.partStreamingClient?.subscribeToPartDeltas(...)

  // Event Handling

  onDAGUpdate(subscriptionId: string, handler: DAGSyncEventHandler): void {
    // Add handler to the set for this subscription
    if (!this.eventHandlers.has(subscriptionId)) {
      this.eventHandlers.set(subscriptionId, new Set());
    }
    this.eventHandlers.get(subscriptionId)!.add(handler);

    // Flush any buffered updates for this subscription
    const pending = this.pendingUpdates.get(subscriptionId);
    if (pending && pending.length > 0) {
      pending.forEach(update => handler(update));
      this.pendingUpdates.delete(subscriptionId);
    }
  }

  removeDAGUpdateHandler(subscriptionId: string, handler: DAGSyncEventHandler): void {
    const handlers = this.eventHandlers.get(subscriptionId);
    if (handlers) {
      handlers.delete(handler);
      // Clean up empty sets
      if (handlers.size === 0) {
        this.eventHandlers.delete(subscriptionId);
      }
    }
  }

  onError(handler: ErrorEventHandler): void {
    this.errorHandlers.add(handler);
  }

  removeErrorHandler(handler: ErrorEventHandler): void {
    this.errorHandlers.delete(handler);
  }

  // Message Processing

  /** A node the graph store holds, or the one this subscription holds (put
   *  into the graph store on the way); undefined when neither does. */
  private endpoint(subscriptionId: string, nodeId: string): any {
    const held = graphStore.getNode(nodeId);
    if (held) return held;
    const fromSub = this.subscriptions.get(subscriptionId)?.nodes.get(nodeId);
    if (fromSub) graphStore.updateNode(fromSub as any);
    return fromSub;
  }

  /**
   * Put an edge into the graph store, forward and reverse — or, while one of
   * its ends is not there yet, HOLD it and put it in when that node lands.
   *
   * Every edge is emitted in canonical origin->dest orientation (source = the
   * node holding the relationship's `out` property), and (rel type, direction)
   * is unique per node type, so the property on each end is derived from the
   * end's class: `resolveRelProperty` is type-aware, which resolves the
   * polymorphic case (`ContainsLogicalPart` → `logical_parts` on Project but
   * `children` on LogicalPart), hence it needs BOTH ends' classes.
   *
   * An end that is missing is normal, not an error: a referent (a User, a
   * Workspace), another island's record or a satellite reaches the client by
   * its own load. These edges used to be dropped, and a later arrival of the
   * node never brought them back.
   */
  private setEdge(
    subscriptionId: string,
    source: string,
    target: string,
    type: string,
    properties?: Record<string, any>,
  ): void {
    const sourceNode = this.endpoint(subscriptionId, source);
    const targetNode = this.endpoint(subscriptionId, target);
    if (!sourceNode || !targetNode) {
      this.holdEdge({ subscriptionId, source, target, type, properties }, !sourceNode, !targetNode);
      return;
    }
    this.dropHeldEdge(`${source}-${type}-${target}`);
    const relClass = RelationshipRegistry[type] as any;
    const forward = resolveRelProperty((ModelRegistry as any)[sourceNode._label], type, 'out', relClass);
    if (!forward) {
      console.warn('Unknown relationship type:', type);
      return;
    }
    // FORWARD index: source (origin) → out-property → [target]
    graphStore.addRelationship(source, target, forward, undefined, { isDagSource: true, properties });
    // REVERSE index: target (dest) → in-property → [source], same properties
    const reverse = resolveRelProperty((ModelRegistry as any)[targetNode._label], type, 'in', relClass);
    if (reverse) {
      graphStore.addRelationship(target, source, reverse, undefined, { isDagSource: true, properties });
    }
  }

  /** Take an edge out of the graph store (forward and reverse); one still
   *  held for a missing end is simply forgotten. `labels`: the ends'
   *  classes as the patch names them (`source_type`, `target_type`). A patch
   *  applies its node changes first, so when it deletes a record AND its
   *  links, that end is gone by now — and without its class the other end's
   *  index kept the link to the deleted record (found by the parity test). */
  private removeEdge(
    subscriptionId: string,
    source: string,
    target: string,
    type: string,
    labels: { source?: string; target?: string } = {},
  ): void {
    const wasHeld = this.heldEdges.has(`${source}-${type}-${target}`);
    this.dropHeldEdge(`${source}-${type}-${target}`);
    if (wasHeld) return; // never reached the store
    const sourceLabel = this.endpoint(subscriptionId, source)?._label ?? labels.source;
    const targetLabel = this.endpoint(subscriptionId, target)?._label ?? labels.target;
    if (!sourceLabel || !targetLabel) return; // nothing to resolve its properties by
    const relClass = RelationshipRegistry[type] as any;
    const forward = resolveRelProperty((ModelRegistry as any)[sourceLabel], type, 'out', relClass);
    if (!forward) return;
    graphStore.removeRelationship(source, target, forward);
    const reverse = resolveRelProperty((ModelRegistry as any)[targetLabel], type, 'in', relClass);
    if (reverse) graphStore.removeRelationship(target, source, reverse);
  }

  private holdEdge(edge: HeldEdge, sourceMissing: boolean, targetMissing: boolean): void {
    const key = `${edge.source}-${edge.type}-${edge.target}`;
    this.dropHeldEdge(key);
    this.heldEdges.set(key, edge);
    for (const [id, missing] of [[edge.source, sourceMissing], [edge.target, targetMissing]] as const) {
      if (!missing) continue;
      let keys = this.edgesWaitingOn.get(id);
      if (!keys) this.edgesWaitingOn.set(id, (keys = new Set()));
      keys.add(key);
    }
  }

  private dropHeldEdge(key: string): void {
    const edge = this.heldEdges.get(key);
    if (!edge) return;
    this.heldEdges.delete(key);
    for (const id of [edge.source, edge.target]) {
      const keys = this.edgesWaitingOn.get(id);
      if (!keys) continue;
      keys.delete(key);
      if (keys.size === 0) this.edgesWaitingOn.delete(id);
    }
  }

  /** A node landed in the graph store (from any load): put in the edges
   *  that were waiting for it, unless their subscription has gone. */
  private releaseEdgesWaitingOn(nodeId: string): void {
    const keys = this.edgesWaitingOn.get(nodeId);
    if (!keys) return;
    for (const key of Array.from(keys)) {
      const edge = this.heldEdges.get(key);
      if (!edge) continue;
      if (!this.subscriptions.has(edge.subscriptionId)) {
        this.dropHeldEdge(key);
        continue;
      }
      this.setEdge(edge.subscriptionId, edge.source, edge.target, edge.type, edge.properties);
    }
  }

  /** THE ONE RULE for what leaves the graph store when a subscription stops
   *  holding it: the island's OWN content (`ownContent`) it held and `keep`
   *  does not, unless another subscription holds it — never a referent (a
   *  User, a Workspace) or another island's row, which other views may show.
   *  Removes the edges now (while both ends are there to resolve their
   *  properties) and answers the records, which the caller removes after
   *  (`dropOwnNodes`). The reload keeps the new state; the final release of a
   *  subscription keeps nothing (VC-UI u38: rows left behind after a page
   *  was left showed as ghosts when the island was opened afresh at another
   *  version, and the store only grew). */
  private dropOwnEdges(
    subscription: DAGSubscription,
    before: Pick<DAGSubscription, 'nodes' | 'relationships'>,
    keepNodes: Set<string>,
    keepRels: Set<string>,
  ): string[] {
    const own = ownContent(subscription);
    const gone = Array.from(before.nodes.entries())
      .filter(([id, n]) => !keepNodes.has(id) && own(n))
      .map(([id]) => id);
    for (const [key, rel] of Array.from(before.relationships)) {
      if (keepRels.has(key) || this.heldByAnotherSubscription(subscription, s => s.relationships.has(key))) continue;
      // an edge is the island's when it touches the island's own content (the
      // recording end holds it); one between two foreign rows is not
      const ends = [before.nodes.get(rel.source), before.nodes.get(rel.target)];
      if (!ends.some(n => n && own(n))) continue;
      this.removeEdge(subscription.subscription_id, rel.source, rel.target, rel.type);
    }
    return gone;
  }

  private dropOwnNodes(subscription: DAGSubscription, ids: string[]): void {
    for (const id of ids) {
      if (!this.heldByAnotherSubscription(subscription, s => s.nodes.has(id))) graphStore.deleteNode(id);
    }
  }

  /** A subscription is released for good (its last view left, after the
   *  unsync debounce, or its opener's release): its own rows leave the store
   *  by the same rule as a reload, keeping nothing. A branch switch never
   *  gets here — the same subscription is synced again within the debounce. */
  private releaseOwnRows(subscription: DAGSubscription | undefined): void {
    if (!subscription) return;
    this.dropOwnNodes(subscription, this.dropOwnEdges(subscription, subscription, new Set(), new Set()));
  }

  /** Does a subscription other than `own` hold this (a record or an edge
   *  several islands' states share, e.g. a referent)? A subscription not yet
   *  re-loaded after a reconnect holds what it held before it. */
  private heldByAnotherSubscription(own: DAGSubscription, holds: (s: DAGSubscription) => boolean): boolean {
    for (const s of Array.from(this.subscriptions.values())) {
      if (s === own) continue;
      if (holds(s)) return true;
      const prior = this.priorState.get(s.subscription_id);
      if (prior && holds({ ...s, ...prior })) return true;
    }
    return false;
  }

  /** What a subscription held before this load: its own state, or — on its
   *  first load after a reconnect — what the previous client held for it. */
  private stateBefore(subscription: DAGSubscription): Pick<DAGSubscription, 'nodes' | 'relationships'> {
    const prior = this.priorState.get(subscription.subscription_id);
    this.priorState.delete(subscription.subscription_id);
    if (subscription.nodes.size === 0 && prior) return prior;
    return subscription;
  }

  /** Take over from the client of the socket before a reconnect: what each
   *  of its subscriptions held, for their first re-load here; then dispose
   *  of it. */
  inheritFrom(previous: DAGSyncClient): void {
    // the packs the page already read: a pack's name is its content, so the
    // re-opens after a reconnect fetch only what they do not hold; and the
    // draft segments it holds, so a draft's re-open names only what came
    // after them. Both are kept under who the data is read as (the held
    // segments' key, and this client's identity listener clears both)
    this.packCache = previous.packCache;
    this.heldSegments = previous.heldSegments;
    this.answeredLane = previous.answeredLane;
    for (const [id, sub] of Array.from(previous.subscriptions)) {
      if (previous.opsOnlySubs.has(id) || sub.nodes.size === 0) continue;
      this.priorState.set(id, { nodes: sub.nodes, relationships: sub.relationships });
    }
    previous.dispose();
  }

  /** Stop touching the graph store: no listener, no timers, no patches. */
  dispose(): void {
    this.disposed = true;
    this.offIdentity();
    this.offNodeCreated();
    this.offPageHide();
    this.prefetcher.dispose();
    for (const t of Array.from(this.storeTimers.values())) clearTimeout(t);
    this.storeTimers.clear();
    for (const t of Array.from(this.quietTimers.values())) clearTimeout(t);
    this.quietTimers.clear();
    for (const s of Array.from(this.serving.values())) {
      if (s.retryTimer) clearTimeout(s.retryTimer);
      if (s.claimsTimer) clearTimeout(s.claimsTimer);
      if (s.supersededTimer) clearTimeout(s.supersededTimer);
      if (s.expandedRetryTimer) clearTimeout(s.expandedRetryTimer);
    }
    for (const t of Array.from(this.memberGenesisTimers)) clearTimeout(t);
    this.memberGenesisTimers.clear();
    this.serving.clear();
    this.loadBuffers.clear();
    this.gapHeld.clear();
    this.subscriptions = new Map();
  }

  private holdEarlyEnrichment(subscriptionId: string, nodeId: string, fields: Record<string, any>): void {
    const held = this.earlyEnrichment.get(nodeId);
    // NEWER WINS: two recomputes of one record, delivered out of order
    if (held && compareComputed(held.fields, fields) > 0) return;
    this.earlyEnrichment.set(nodeId, { subscriptionId, fields: { ...(held?.fields ?? {}), ...fields } });
  }

  /** A record landed in the graph store: lay the computed values held for it
   *  over it, in the graph store and in every subscription that holds it.
   *  NEWER WINS, by `evaluated_at` when both carry one; otherwise the
   *  record's own value is kept (its load read it after the patch was
   *  computed) and a held field fills only what the record lacks — which is
   *  every computed field once the state stops carrying them. */
  private applyEarlyEnrichment(node: any): void {
    const id = node?.id;
    const held = id ? this.earlyEnrichment.get(id) : undefined;
    if (!held) return;
    this.earlyEnrichment.delete(id);
    const order = compareComputed(held.fields, node);
    if (order < 0) return;
    const fields = order > 0
      ? held.fields
      : Object.fromEntries(Object.entries(held.fields).filter(([k]) => node[k] === undefined));
    if (Object.keys(fields).length === 0) return;
    const merged = { ...node, ...fields };
    const ModelClass = (ModelRegistry as any)[node._label];
    let instance: any = merged;
    try {
      if (ModelClass?.load) instance = ModelClass.load(merged);
    } catch { /* keep the merged data, as the patch path does */ }
    graphStore.updateNode(instance);
    for (const s of Array.from(this.subscriptions.values())) {
      if (s.nodes.has(id)) s.nodes.set(id, instance);
    }
  }

  /** COMPUTED-VALUES CHANNEL items (wire contract §4; `{id, _label, _v,
   *  ...derived fields}`): laid over the record the store holds when newer
   *  by `_v`, or held for a record not loaded yet and laid over it when it
   *  lands (fix 4). Equal `_v` = the same write: nothing to do. */
  applyComputed(subscriptionId: string, items: { id: string; _label?: string; _v?: number | null; [k: string]: unknown }[]): void {
    for (const item of items) {
      const { id, _label, ...fields } = item;
      const node = graphStore.getNode(id) as any;
      if (!node) {
        this.holdEarlyEnrichment(subscriptionId, id, fields);
        continue;
      }
      // OLDER LOSES; an EQUAL `_v` is the same write, applied again: a live
      // frame carries part of an item's fields, its since the whole item
      // with the same `_v` (vc-impl's recordings, 2026-10-08)
      if (typeof node._v === "number") {
        const order = compareComputed(fields, node);
        if (order < 0 || (order === 0 && typeof fields._v !== "number")) continue;
      }
      const merged = { ...node, ...fields };
      const ModelClass = (ModelRegistry as any)[node._label ?? _label];
      let instance: any = merged;
      try {
        if (ModelClass?.load) instance = ModelClass.load(merged);
      } catch { /* keep the merged data, as the patch path does */ }
      graphStore.updateNode(instance);
      for (const s of Array.from(this.subscriptions.values())) {
        if (s.nodes.has(id)) s.nodes.set(id, instance);
      }
    }
  }

  /** COMPUTED-VALUES CHANNEL tombstones: an item that left the lane's view
   *  (its record was deleted, its copy dropped). What is held for it goes;
   *  the record itself leaves through the state, not through this channel. */
  dropComputed(ids: string[]): void {
    for (const id of ids) this.earlyEnrichment.delete(id);
  }

  /** A subscription ended: the edges it held for missing ends go with it. */
  private dropHeldEdgesOf(subscriptionId: string): void {
    for (const [key, edge] of Array.from(this.heldEdges)) {
      if (edge.subscriptionId === subscriptionId) this.dropHeldEdge(key);
    }
  }

  /** A subscription ended: what it held for records not loaded yet — edges
   *  and computed values — goes with it. */
  private forgetHeldFor(subscriptionId: string): void {
    const serving = this.serving.get(subscriptionId);
    if (serving?.retryTimer) clearTimeout(serving.retryTimer);
    if (serving?.claimsTimer) clearTimeout(serving.claimsTimer);
    if (serving?.supersededTimer) clearTimeout(serving.supersededTimer);
    if (serving?.expandedRetryTimer) clearTimeout(serving.expandedRetryTimer);
    this.serving.delete(subscriptionId);
    this.dropHeldEdgesOf(subscriptionId);
    this.seenOps.delete(subscriptionId);
    this.gapHeld.delete(subscriptionId);
    this.gapCatchUps.delete(subscriptionId);
    const quiet = this.quietTimers.get(subscriptionId);
    if (quiet) clearTimeout(quiet);
    this.quietTimers.delete(subscriptionId);
    for (const [id, held] of Array.from(this.earlyEnrichment)) {
      if (held.subscriptionId === subscriptionId) this.earlyEnrichment.delete(id);
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketResponse = JSON.parse(event.data);
      if ((message as { claims?: unknown }).claims === true) {
        this.onClaimsFrame(message as unknown as { context_id?: string; branch?: string; items?: unknown[] });
        return;
      }
      if ((message as { type?: string }).type === 'dag-gen-superseded') {
        this.onGenSuperseded(message as unknown as { context_type: string; context_id: string; gen: string });
        return;
      }
      if ((message as { type?: string }).type === 'commit_progress') {
        // my own commit's steps (the user's own channel): the commit screen
        // reads them from the store, whichever view opened the socket
        noteCommitProgress(message);
        return;
      }
      if ((message as { type?: string }).type === 'dag-subscription-revoked') {
        this.onSubscriptionRevoked(message as unknown as { context_type?: string; context_id?: string; reason?: string; status?: number });
        return;
      }

      switch (message.type) {
        case 'sync-dag-response':
          if (this.serving.has((message as SyncDAGResponse).subscription_id ?? "")) {
            // the serving open loads it; the socket only delivers frames —
            // and this answer says the server now sends them
            this.onServingAck((message as SyncDAGResponse).subscription_id!);
            break;
          }
          if (this.opsOnlySubs.has((message as SyncDAGResponse).subscription_id ?? "")) {
            // ops-follow subscription: no state hydration wanted — the
            // subscription exists purely to route dag_patch (and its ops)
            this.handleSyncDAGResponse(message as SyncDAGResponse);
          }
          // any other answer is the socket's acknowledgement: the state
          // comes through the serving open
          break;
        case 'dag_patch':
          this.handleDAGPatch(message);
          break;
        case 'subscribe_part_deltas_response':
        case 'subscribe_task_deltas_response':
        case 'delta':
        case 'resubscribe_required':
          // Forward part streaming messages to part streaming client: every
          // type it handles (a dropped `resubscribe_required` left a slow
          // consumer cut off for good)
          if (this.partStreamingClient) {
            this.partStreamingClient.handleMessage(message as any);
          }
          break;
        case 'error': {
          // THE ONE REFUSAL FRAME (VC-D's refusal set 2, 2026-10-14):
          // `{type: "error", status: <HTTP int>, code: <string>, reason,
          // failed, next}` plus the routing keys.
          const m = message as any;
          const errText = m.reason ?? 'Unknown error';
          const ctxType = (message as any).context_type;
          const ctxId = (message as any).context_id;
          if (!(ctxType && ctxId) && (m.task_id || m.part_id)) {
            // a part's or a task's live stream refused (404 not_found when
            // not readable): that chat's stream, never every view on the page
            this.partStreamingClient?.handleMessage(message as any);
            break;
          }
          const errSub =
            ctxType && ctxId ? this.subscriptions.get(`${ctxType}:${ctxId}`) : undefined;
          if (errSub) {
            // Context-tagged error → belongs to ONE subscription. Setting it
            // there (instead of the global broadcast) stops a ChatTask 404
            // from painting every mounted DAGLayout red.
            errSub.error = errText;
            // a load that failed ends: the retry re-subscribes and buffers anew
            this.loadBuffers.delete(errSub.subscription_id);
            // the HTTP status: `status` in the uniform frame (whose `code` is
            // then the string code); an older frame's `code` was the number.
            // useDAGSync retries a 404 creation race on it.
            errSub.errorCode = typeof m.status === 'number' ? m.status : undefined;
            errSub.isLoading = false;
            this.notifySubscriptionUpdate(errSub);
          } else {
            // Connection-level / untagged error — all views affected.
            this.notifyErrorHandlers(errText);
          }
          break;
        }
        case 'ping':
          // Server ping - no action needed, connection is alive
          break;
        case 'unsync-dag-response':
          // DAG unsubscribe response - no action needed
          break;
        default:
          // Unhandled DAG sync message type
      }
    } catch (error) {
      console.error('Error parsing DAG sync message:', error, event.data);
    }
  }

  /**
   * Transform node data to match Zod schemas before validation
   * Handles common mismatches between backend data format and TypeScript schemas
   *
   * Note: JSON-serialized fields (OptionalJson/Json) are handled by z.preprocess
   * in the generated Zod schemas — no manual parsing needed here.
   */
  private transformNodeDataForValidation(nodeData: any): void {
    Object.keys(nodeData).forEach(key => {
      // Skip special fields
      if (key === 'id' || key.startsWith('_') || key.startsWith('dag_')) {
        return;
      }

      const value = nodeData[key];

      // Remove null relationship fields (Zod expects undefined or valid objects)
      if (value === null) {
        delete nodeData[key];
        return;
      }
    });
  }

  private handleSyncDAGResponse(response: SyncDAGResponse): void {
    if (this.disposed) return;
    const subscription = this.subscriptions.get(response.subscription_id);
    if (!subscription) {
      console.warn('Received sync-dag-response for unknown subscription:', response.subscription_id);
      return;
    }
    this.adoptLane(subscription, response);

    // Backend returns neograph format: { root_nodes: [...], nodes: [...], relationships: [...] }
    // Parse with NeoGraphParser to reconstruct relationships, then instantiate as class instances

    // Convert response to NeoGraphFormat (map 'type' to 'rel')
    const neoGraphData = {
      root_nodes: response.root_nodes,
      nodes: response.nodes,
      relationships: response.relationships.map(rel => ({
        source: rel.source,
        target: rel.target,
        rel: (rel as any).rel || rel.type  // Map 'type' to 'rel' for NeoGraphParser
      })),
      timestamp: response.timestamp
    };

    const parser = new NeoGraphParser();
    const graphNodes = parser.parse(neoGraphData);

    // IMPORTANT: parser.parse() only returns ROOT nodes, but ALL nodes are in parser.getAllNodes()
    // instantiate ALL nodes, not just roots: every node is read from the graph store
    const allGraphNodes = Array.from(parser.getAllNodes().values());

    // Don't use toPlainObject() - it reconstructs ALL relationships including reverse ones
    // Instead, use raw node data and let .load() only instantiate what was explicitly queried
    // Relationships are handled separately via graphStore.addRelationship() below
    const instances = allGraphNodes.map((gn: any) => {
      const nodeData = gn.data; // Raw node data without reconstructed relationships

      // Transform data to match schemas before instantiation
      this.transformNodeDataForValidation(nodeData);

      const label = nodeData._label;
      // Use cached ModelRegistry (eagerly loaded in syncDAG)
      const ModelClass = ModelRegistry[label];
      if (ModelClass && ModelClass.load) {
        try {
          // Pass raw node data - relationships will be accessed via GraphStore
          const instance = ModelClass.load(nodeData);
          return instance;
        } catch (error) {
          console.error(`Failed to instantiate ${label}:`, error);
          return nodeData; // Fallback to plain object on error
        }
      }
      return nodeData; // Fallback to plain object
    });

    // A RELOAD REPLACES THE STATE (VC-UI fix 2, serving brief §8e): what
    // the previous state held and this one does not was deleted while I was
    // away (a reconnect re-loads; the ops before the new cursor are not
    // replayed), so it leaves the graph store too — edges now, while both
    // ends are still there to resolve their properties; records after the
    // new state is in. Unless another subscription still holds it.
    // ONLY THE ISLAND'S OWN CONTENT (`ownContent`): a state also carries
    // referent rows (a User, a Workspace) and other islands' rows, which may
    // drop out of it while they still exist and other views show them.
    const newNodeIds = new Set(instances.map((i: any) => i?.id).filter(Boolean));
    const newRelKeys = new Set(
      response.relationships.map(rel => {
        const t = (rel as any).relationship_type || (rel as any).rel || rel.type;
        return `${rel.source}-${t}-${rel.target}`;
      }),
    );
    const goneNodeIds = this.dropOwnEdges(subscription, this.stateBefore(subscription), newNodeIds, newRelKeys);
    this.dropHeldEdgesOf(subscription.subscription_id);

    // Clear existing data
    subscription.nodes.clear();
    subscription.relationships.clear();

    // Store ALL instances (not just roots)
    instances.forEach((instance: any) => {
      if (instance?.id) {
        subscription.nodes.set(instance.id, instance);

        // IMPORTANT: Also populate GraphStore for reactive access
        graphStore.updateNode(instance);
      }
    });

    // Debug: lets us inspect what comes in for a specific node id —
    // set `window.__debugNode = "<uuid>"` in devtools OR
    // `localStorage.setItem('__debugNode', '<uuid>')` (survives navigation).
    const debugNodeId =
      typeof window !== "undefined"
        ? (window as any).__debugNode ||
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("__debugNode")) ||
          undefined
        : undefined;
    if (debugNodeId) {
      const matchingNodes = response.nodes.filter(
        (n: any) => n.id === debugNodeId
      );
      const matchingRels = response.relationships.filter(
        (r: any) => r.source === debugNodeId || r.target === debugNodeId
      );
      console.log(
        `%c[DAG-DEBUG] ${debugNodeId} in sync-dag-response:`,
        "color: purple; font-weight: bold;",
        {
          nodeOccurrences: matchingNodes.length,
          relationshipsInvolvingIt: matchingRels.length,
          relsWithItAsSource: matchingRels
            .filter((r: any) => r.source === debugNodeId)
            .map((r: any) => ({
              type: (r as any).relationship_type || (r as any).rel || r.type,
              target: r.target.slice(0, 8),
            })),
          relsWithItAsTarget: matchingRels
            .filter((r: any) => r.target === debugNodeId)
            .map((r: any) => ({
              type: (r as any).relationship_type || (r as any).rel || r.type,
              source: r.source.slice(0, 8),
            })),
        }
      );
    }

    // Store relationships for direct access; the graph store gets each one
    // once both its ends are there (`setEdge` holds it until then)
    response.relationships.forEach(rel => {
      const relationshipType = (rel as any).relationship_type || (rel as any).rel || rel.type;
      const relationshipKey = `${rel.source}-${relationshipType}-${rel.target}`;
      subscription.relationships.set(relationshipKey, { ...rel, type: relationshipType });
      // Edge properties arrive with the island's rows (the serving open ships
      // every link's properties) — passed through exactly like the patch path
      // does so `useNodesWithRel` / `rel.properties` reads work on first load.
      this.setEdge(
        subscription.subscription_id, rel.source, rel.target, relationshipType,
        (rel as any).properties as Record<string, any> | undefined,
      );
    });

    this.dropOwnNodes(subscription, goneNodeIds);

    subscription.version = response.version;
    subscription.timestamp = response.timestamp;
    subscription.isLoading = false;
    subscription.error = undefined;
    subscription.errorCode = undefined;

    this.notifySubscriptionUpdate(subscription);
  }

  /** Subscribe to raw OP events (RECORD_DELTAs and friends riding each
   *  dag_patch). Returns an unsubscribe function. */
  onOps(
    // `branch`: the lane the frame was tagged with, passed through so a
    // consumer can tell its own lane's ops from its base's. An overlay
    // subscriber receives its base's frames too (branch_visible); without
    // this a page could not tell which lane an op belonged to.
    handler: (e: { context_type: string; context_id: string; ops: unknown[]; branch?: string }) => void
  ): () => void {
    this.opsHandlers.add(handler);
    return () => this.opsHandlers.delete(handler);
  }

  /** FOLLOW an island's ops without hydrating any state: a light, string-
   *  typed subscription that exists purely so the server routes dag_patch
   *  (and the ops riding it) to this socket. Returns a disposer. */
  async followOps(contextType: string, contextId: string): Promise<() => void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      await this.connect();
    }
    if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
      await this.waitForConnection();
    }
    const subId = `${contextType}:${contextId}`;
    const refs = this.subscriptionRefCounts.get(subId) || 0;
    this.subscriptionRefCounts.set(subId, refs + 1);
    if (refs === 0 && !this.subscriptions.has(subId)) {
      this.opsOnlySubs.add(subId);
      // full DAGSubscription shape — handleSyncDAGResponse/handleDAGPatch
      // touch nodes/relationships even for an ops-only follow
      const branch = activeBranchFor(contextId);
      this.subscriptions.set(subId, {
        subscription_id: subId,
        context_type: contextType,
        context_id: contextId,
        branch,
        nodes: new Map(),
        relationships: new Map(),
        version: 0,
        timestamp: new Date().toISOString(),
        isLoading: true,
      } as unknown as DAGSubscription);
      this.websocket!.send(
        JSON.stringify({
          type: "sync-dag",
          subscription_id: subId,
          context_type: contextType,
          context_id: contextId,
          light: true,
          branch,
        })
      );
    }
    return () => {
      const n = (this.subscriptionRefCounts.get(subId) || 1) - 1;
      this.subscriptionRefCounts.set(subId, n);
      // the follow was the last hold on a subscription a page view made
      // full (the view left first: React cleans a layout up before the
      // header inside it) — the same final release as the view's
      if (n <= 0 && !this.opsOnlySubs.has(subId) && this.subscriptions.has(subId)) {
        this.scheduleFinalRelease(subId, contextType, contextId);
      } else if (n <= 0 && this.opsOnlySubs.has(subId)) {
        this.opsOnlySubs.delete(subId);
        this.subscriptions.delete(subId);
        this.forgetHeldFor(subId);
        this.subscriptionRefCounts.delete(subId);
        try {
          this.websocket?.send(
            JSON.stringify({
              type: "unsync-dag",
              context_type: contextType,
              context_id: contextId,
            })
          );
        } catch { /* socket may be gone */ }
      }
    };
  }

  /** Subscribe to record-announce events (ChangeImpact/AnalysisRecord etc.).
   *  Returns an unsubscribe function. */
  onRecordAnnounce(handler: (a: RecordAnnounceEvent) => void): () => void {
    this.recordAnnounceHandlers.add(handler);
    return () => this.recordAnnounceHandlers.delete(handler);
  }

  /** REST base derived from the websocket URL (ws(s)://host[/path] → http(s)://host). */
  private restBase(): string | null {
    try {
      // The app constructs this client around an EXISTING shared socket
      // (websocketUrl === '') — derive the HTTP base from the live
      // socket's own absolute url, falling back to the env the URL-based
      // path uses. Without this, the op catch-up silently degraded to the
      // empty snapshot in the app (empty project views, 2026-08-27).
      const raw =
        this.websocketUrl ||
        this.websocket?.url ||
        (typeof process !== 'undefined'
          ? process.env.NEXT_PUBLIC_SOURCEPILOT_WS_URL
          : undefined) ||
        '';
      if (!raw) return null;
      const u = new URL(raw);
      const proto = u.protocol === 'wss:' ? 'https:' : 'http:';
      return `${proto}//${u.host}`;
    } catch {
      return null;
    }
  }

  /** The server decides which LANE a subscription follows — under implicit
   *  overlays a page bound to `main` follows MY overlay on main — and says
   *  so in the sync response. Patches are filtered by the subscription's
   *  lane (`branchVisible`), so without adopting it every op I make in my
   *  own overlay was dropped here as "a branch I cannot see". */
  private adoptLane(subscription: { branch?: string }, response: unknown): void {
    const lane = (response as { branch?: unknown } | null)?.branch;
    if (typeof lane === 'string' && lane) subscription.branch = lane;
  }

  /** The lane a subscription's catch-up reads: the adopted one. */
  private laneOf(contextId: string, subscriptionId: string): string {
    return this.subscriptions.get(subscriptionId)?.branch ?? activeBranchFor(contextId);
  }


  /** THE LOAD (the serving open: wire contract §1–§2, §5). One
   *  subscription's island, and the islands the server EXPANDS it with (its
   *  load spec's sub-tasks, system prompt, references — what the include
   *  specs loaded before):
   *    1. POST /versioning/open — refused (403/404, or 409 genesis_pending,
   *       re-asked after `retry_after_ms` for up to GENESIS_BUDGET_MS) or
   *       answered with where each island's state lives and the open's
   *       cursor (a watermark taken before anything was read);
   *    2. subscribe each island on the socket, naming its draft's `gen`
   *       (frames meanwhile go into each one's load buffer, as today);
   *    3. fetch every island's packs, commit files, draft segments and
   *       unversioned rows — then apply them in ONE synchronous step, the
   *       expanded islands first, so the requested one ends its loading with
   *       its whole group in the store;
   *    4. catch up each from the open's cursor, then apply its buffer, minus
   *       what the load reflects; open the computed channels.
   *  A load failing after the open is opened again after OPEN_RETRY_MS's
   *  pauses, then the subscription errs 503, as a refusal does. */
  private async openServing(
    subscription: DAGSubscription,
    opts: { reopen?: boolean } = {},
  ): Promise<void> {
    const subId = subscription.subscription_id;
    const base = this.restBase();
    const entry = this.serving.get(subId) ?? { gen: null, fs: '', lane: subscription.branch ?? 'main', subscribed: false };
    this.serving.set(subId, entry);
    entry.opening = true;
    entry.again = false;
    if (!this.loadBuffers.has(subId)) this.loadBuffers.set(subId, []);
    const current = () => !this.disposed && this.subscriptions.get(subId) === subscription && this.serving.get(subId) === entry;
    let targets: ServingTarget[] = [];
    let flush = true;
    // THE DRAW RACES THE OPEN: once the open has answered, a stored entry
    // read later is never drawn over its answer (two versions on one page)
    let answered = false;
    let drawing: Promise<void> = Promise.resolve();
    try {
      if (!base) throw new Error('no REST base');
      const asked = subscription.branch;
      const opening = postOpen(fetch, base, {
        islands: [{
          type: subscription.context_type, id: subscription.context_id,
          ...(subscription.branch ? { branch: subscription.branch } : {}),
          ...this.lastSegmentFor(subscription.context_id, subscription.branch),
          ...(subscription.claimsStale ? { claims: { stale: subscription.claimsStale } } : {}),
        }],
        channels: ['state', 'computed', 'claims', 'unversioned'],
      }, branchHeaders());
      opening.catch(() => {}); // awaited below; never unhandled while the draw runs
      // while the open is in flight, a refresh draws what this browser kept
      // (Tom, 2026-10-19); the answer then confirms, replaces or clears it
      if (!opts.reopen) drawing = this.drawBeforeOpen(subscription, entry, () => current() && !answered);
      const answer = await opening;
      answered = true;
      if (!current()) { flush = false; return; }
      const refusal = (answer.refused ?? []).find(r => r.island === subscription.context_id);
      // answered — unless genesis is pending: the drawn page then waits for the re-open
      if (!refusal || genesisRetryMs(refusal) === null) markAnswered(subscription.context_id);
      if (refusal && genesisRetryMs(refusal) === null) this.clearDrawn(subscription, entry);
      if (refusal) {
        // refused (403, 404, or 409 other_organisation): what this browser
        // kept for the island goes; genesis_pending keeps it
        if (genesisRetryMs(refusal) === null) void this.forgetStored(subscription.context_id);
        flush = !this.refuseServing(subscription, entry, refusal, opts);
        return;
      }
      const island = answer.islands.find(i => i.island === subscription.context_id && i.opened_by?.includes('requested'))
        ?? answer.islands.find(i => i.island === subscription.context_id);
      if (island) this.answeredLane.set(this.heldKey(subscription.context_id, asked), island.lane);
      // the holder's pin version alerts and retired references, as of this
      // open (both as served to this reader)
      if (island) setPinConflicts(subscription.context_id, island.pin_conflicts);
      if (island) setRetiredPins(subscription.context_id, island.retired_pins);
      if (island && storableLane(island.lane, userOf(getServingIdentity())) && !island.at) {
        void this.queueWrite(subscription.context_id, () => this.islandStore.setLaneFor(subscription.context_id, asked ?? DEFAULT_LANE, island.lane));
      }
      if (!island) {
        this.clearDrawn(subscription, entry);
        void this.forgetStored(subscription.context_id);
        this.failServing(subscription, NOT_AVAILABLE, 404);
        flush = false;
        return;
      }
      entry.genesisSince = undefined;
      targets = [
        ...answer.islands.filter(i => i !== island)
          .map(i => this.memberFor(subId, i))
          .filter((t): t is ServingTarget => !!t),
        { subscription, entry, island },
      ];
      flush = false; // loadTargets flushes each island it loads
      this.retryExpandedGenesis(subscription, entry, answer.refused ?? []);
      await this.loadTargets(base, targets, answer);
      entry.reopenedOn410 = false;
      entry.failures = 0;
    } catch (e) {
      // the session ended on the server: as a sign-out (VC-Authz, 2026-10-19)
      if (e instanceof OpenFailed && e.status === 401) sessionEnded();
      // no answer: what the store holds may still be drawn (offline) — let
      // its read finish before deciding what the failure does to it
      await drawing;
      if (!current()) return;
      // a drawn page the open could not confirm: refused as a whole (401,
      // 403, 404) it is cleared; with no answer (offline, the server down,
      // a 5xx) it stays drawn, marked, with writes paused — and nothing
      // more is drawn (VC-core, VC-Authz 2026-10-19)
      if (entry.drawn) {
        if (e instanceof OpenFailed && [401, 403, 404].includes(e.status)) {
          if (e.status !== 401) void this.forgetStored(subscription.context_id);
          this.clearDrawn(subscription, entry);
          this.failServing(subscription, 'not available', e.status);
          flush = false;
          return;
        }
        if (!(e instanceof OpenFailed && e.status === 410)) markOffline(subscription.context_id);
      }
      if (e instanceof OpenFailed && e.status === 410) void this.forgetStored(subscription.context_id);
      if (e instanceof OpenFailed && e.status === 410 && !entry.reopenedOn410) {
        // an op file under a field set (or a draft gen) the server no longer
        // serves: open again at once — once per load, not a failure — and
        // with nothing held for it (its files are of the old field set)
        this.heldSegments.delete(this.heldKey(subscription.context_id, subscription.branch));
        entry.reopenedOn410 = true;
        entry.again = true;
        flush = false;
        return;
      }
      // the load failed after the open (a pack, a file, a re-asked link):
      // open again after a pause, then err as a refusal does
      flush = false;
      const failures = (entry.failures ?? 0) + 1;
      entry.failures = failures;
      // a drawn page waiting for its first answer keeps asking at the last
      // pause, drawn and marked, rather than failing
      const wait = DAGSyncClient.OPEN_RETRY_MS[failures - 1]
        ?? (entry.drawn ? DAGSyncClient.OPEN_RETRY_MS[DAGSyncClient.OPEN_RETRY_MS.length - 1] : undefined);
      if (wait !== undefined) {
        entry.retryTimer = setTimeout(() => {
          entry.retryTimer = undefined;
          if (current()) void this.openServing(subscription, opts);
        }, wait);
      } else {
        for (const t of targets) if (t.subscription !== subscription) this.failServing(t.subscription, 'state unavailable', 503);
        this.failServing(subscription, 'state unavailable', 503);
      }
    } finally {
      entry.opening = false;
      if (flush) this.flushLoadBuffer(subId, undefined, null);
      if (entry.again && current()) this.reopenServing(subId);
    }
  }

  /** Steps 2–4 for the islands of one open answer. */
  private async loadTargets(base: string, targets: ServingTarget[], answer: OpenAnswer): Promise<void> {
    // what this page applied and has not written yet goes into the list
    // FIRST: a re-open reads the stored entry back below, and a tail left
    // unwritten was gone from the list while the cursor moved past it (Tom,
    // stack 3, 2026-10-27: a chat's tool cards spun for good, refresh or not)
    await Promise.all(targets.map(t => this.writeUnwritten(t.subscription.subscription_id)));
    for (const t of targets) {
      const { subscription, entry, island } = t;
      const at = typeof island!.at === 'string' && island!.at ? island!.at : undefined;
      if (at) {
        // answered AT a pin: that commit only. A live subscription of it from
        // before (it was answered at head, or its page let it go) ends.
        if (entry.subscribed && this.websocket?.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify({ type: 'unsync-dag', context_type: subscription.context_type, context_id: subscription.context_id }));
        }
        entry.at = at;
        entry.subscribed = false;
        entry.gen = island!.gen;
        entry.fs = answer.fs;
        entry.openedBy = island!.opened_by ?? [];
        setPinnedIsland(subscription.context_id, at, computedUnavailable(island!.computed));
        continue;
      }
      if (entry.at) { entry.at = undefined; forgetPinnedIsland(subscription.context_id); }
      const rebind = !entry.subscribed || entry.gen !== island!.gen || entry.lane !== island!.lane;
      // a re-open that answers ANOTHER gen than the one last superseded ends
      // that episode: a later supersede is a new one, re-opened at once
      if (entry.supersededGen !== undefined && island!.gen !== entry.supersededGen) {
        entry.supersededGen = undefined;
        entry.supersededTries = 0;
      }
      entry.gen = island!.gen;
      entry.fs = answer.fs;
      entry.lane = island!.lane;
      entry.line = island!.line;
      entry.epoch = island!.segment_epoch ?? null;
      entry.openedBy = island!.opened_by ?? [];
      subscription.branch = island!.lane;
      if (rebind) this.sendSubscribe(subscription, island!.gen);
      entry.subscribed = true;
    }
    // an island this browser kept at the same coordinate is not fetched: its
    // base comes from the pack cache and its list stands for the commits
    // after it, the draft's segments and the edits since; the tail catches it
    // up from the stored cursor (stage 2, shape B)
    const stored = await Promise.all(targets.map(t => this.storedFor(t, answer.fs, answer.cursor)));
    const fetched = await Promise.all(targets.map((t, k) => stored[k]
      ? postUnversioned(fetch, base, t.island!.island, t.island!.unversioned ?? []).catch((): UnversionedRow[] => [])
      : this.fetchIsland(base, t.island!, answer.fs)));
    const live = targets.map(t => !this.disposed && this.subscriptions.get(t.subscription.subscription_id) === t.subscription);
    targets.forEach((t, k) => {
      if (!live[k]) return;
      // a page drawn from the store before the open: confirmed when the
      // answer stands for the same entry, else cleared first — the answer
      // is never laid over it
      const drawn = t.entry.drawn;
      const held = stored[k]?.held;
      const confirmed = !!drawn && !!held && held.lane === drawn.held.lane && held.cursor === drawn.held.cursor;
      if (drawn && !confirmed) this.clearDrawn(t.subscription, t.entry);
      if (drawn) markAnswered(t.subscription.context_id);
      t.entry.drawn = undefined;
      // THE LIST: recorded from here, after a stored island's own list is
      // replayed (that is in the store already) and before the catch-up
      // (its ops are the list's next ones); a cold load's from before its
      // commit files and segments, which follow its base
      const subId = t.subscription.subscription_id;
      const keep = !t.entry.at && this.islandStore !== noIslandStore && storableLane(t.island!.lane, userOf(getServingIdentity()));
      this.coldBase.delete(subId);
      if (stored[k]) {
        this.applyStored(t, stored[k]!, fetched[k] as UnversionedRow[], { rowsDrawn: confirmed });
        if (keep) this.storeLog.set(subId, { patches: [], seq: held!.count });
        else this.storeLog.delete(subId);
      } else {
        const loaded = fetched[k] as Awaited<ReturnType<DAGSyncClient['fetchIsland']>>;
        if (keep) {
          this.storeLog.set(subId, { patches: [], seq: 0 });
          this.coldBase.set(subId, loaded[0].base);
        } else this.storeLog.delete(subId);
        this.applyIsland(t, loaded, answer.cursor);
      }
    });
    // a stored LINE catches up by cursor alone: a line's gen names its head
    // now, and the gen-bound tail would start there, past the commits since
    // the stored cursor (VC-D, 2026-10-19); a stored draft keeps its gen
    const reflected = await Promise.all(targets.map((t, k) =>
      live[k] && !t.entry.at ? this.catchUp(t.subscription.context_id, t.subscription.subscription_id, {
        duringLoad: true,
        ...(stored[k] ? { maxOps: STORE_MAX_CATCHUP_OPS } : {}),
        ...(stored[k] && !isDraftLane(t.island!.lane) ? { byCursor: true } : {}),
      }) : Promise.resolve(null)));
    // a stored island that did not catch up (the tail refused or failed) is
    // never shown as current: its entry goes and the island loads cold
    const behind = targets.filter((t, k) => live[k] && stored[k] && reflected[k] === null);
    if (behind.length) {
      for (const t of behind) this.storeLog.delete(t.subscription.subscription_id);
      await Promise.all(behind.map(t => this.forgetStored(t.island!.island, t.island!.lane)));
      throw new OpenFailed('stored state did not catch up', 410);
    }
    // a cold load's head: its base, and the patches applied after it (its
    // commit files, segments and catch-up), at the cursor they reach. It
    // replaces what was kept for the lane.
    targets.forEach((t, k) => {
      const subId = t.subscription.subscription_id;
      const base = this.coldBase.get(subId);
      this.coldBase.delete(subId);
      const log = this.storeLog.get(subId);
      if (!live[k] || !base || !log) return;
      const island = t.island!;
      const cursor = this.catchupCursors.get(subId);
      if (cursor === undefined || reflected[k] === null) { this.storeLog.delete(subId); return; }
      const patches = log.patches.splice(0);
      log.seq = patches.length;
      this.queueWrite(island.island, () => this.islandStore.putBase({
        format: STORE_FORMAT, build: CLIENT_BUILD, island: island.island, lane: island.lane, line: island.line,
        gen: island.gen ?? null, epoch: island.segment_epoch ?? null, fs: answer.fs, cursor, base, count: patches.length,
      }, patches));
    });
    targets.forEach((t, k) => {
      if (!live[k]) return;
      // the client opens BEFORE it subscribes (the subscribe names the gen
      // the open answered): an op published after this catch-up read but
      // before the server took the subscribe reached neither — the ack then
      // runs one more catch-up
      if (this.serving.get(t.subscription.subscription_id)?.acked === false) {
        const e = this.serving.get(t.subscription.subscription_id)!;
        e.caughtUpUnacked = true;
      }
      this.flushLoadBuffer(t.subscription.subscription_id, answer.cursor, reflected[k]);
      // a pinned open serves no computed values and no claims
      if (t.entry.at) return;
      this.openComputed(base, t);
      this.openClaims(base, t);
      this.scheduleStore(t.subscription.subscription_id);
      // the open's base is behind its line's head (the snapshot writer has
      // not stored it yet, or a draft's line moved on): the head's packs in
      // idle time, for the next cold open
      if ((t.island!.state.commits_after_base ?? []).length > 0) {
        this.prefetcher.want(t.island!.island, t.island!.line, t.island!.head);
      }
    });
  }

  /** The stored head and list that may stand for this target's load, with
   *  its base's rows when the page has not drawn it yet; or none. One that
   *  may not (another field set, lane, gen or epoch, another client build,
   *  or too far behind) is deleted: it is never laid over a cold load. A
   *  base whose packs the pack cache no longer holds loads cold (and that
   *  load records a new head). */
  private async storedFor(t: ServingTarget, fs: string, openCursor: number): Promise<{ held: HeldIsland; rows?: BaseRows } | undefined> {
    const island = t.island!;
    if (t.entry.at || (typeof island.at === 'string' && island.at)) return undefined;
    if (!storableLane(island.lane, userOf(getServingIdentity()))) return undefined;
    // the entry the page drew before the open: already in hand, and drawn
    const drawn = t.entry.drawn?.held;
    const inHand = drawn ?? t.entry.read;
    t.entry.read = undefined;
    const held = inHand && inHand.lane === island.lane ? inHand : await this.islandStore.get(island.island, island.lane);
    if (!entryMatches(held, { fs, lane: island.lane, line: island.line, gen: island.gen ?? null, epoch: island.segment_epoch ?? null })
      || !withinCatchUp(held.cursor, openCursor) || held.replay > STORE_MAX_CATCHUP_OPS
      // a committed line's entry stands only on the base the open answers:
      // nothing can show its list covers the commits since its own, and a
      // hole in it never healed (the tail starts at its cursor). A new
      // commit's packs are prefetched, so the cold load downloads none.
      || (!isDraftLane(island.lane) && held.base.commit !== openBaseCommit(island))) {
      if (held) await this.forgetStored(island.island, island.lane);
      return undefined;
    }
    if (held === drawn) return { held };
    const rows = await this.baseRows(held.base);
    return rows ? { held, rows } : undefined;
  }

  /** A head's base, from the pack cache: every pack, or none. */
  private async baseRows(base: StoredBase): Promise<BaseRows | null> {
    const packs = await Promise.all(base.packs.map(p => this.packCache.get(p.name).catch(() => undefined)));
    if (packs.some(r => !r)) return null;
    const rows: BaseRows = { root_nodes: [], nodes: [], relationships: [] };
    base.packs.forEach((p, k) => {
      for (const r of packs[k] as Record<string, unknown>[]) {
        if (p.kind === 'edge') rows.relationships.push(r);
        else (r.id === base.root ? rows.root_nodes : rows.nodes).push(r);
      }
    });
    return rows;
  }

  /** A stored island into the store, as a load applies its state: its base
   *  and list replace what the subscription held, and its cursor is the
   *  stored one, so the load's catch-up brings it up to now. */
  private applyStored(t: ServingTarget, stored: { held: HeldIsland; rows?: BaseRows }, rows: UnversionedRow[], opts: { rowsDrawn?: boolean } = {}): void {
    const { subscription, island } = t;
    const subId = subscription.subscription_id;
    // the subscription's state is the stored one now: what this page
    // applied before is not, so an op past the stored cursor is applied
    // again (and recorded), never dropped as "already applied"
    this.seenOps.delete(subId);
    this.applyUnversioned(rows);
    t.entry.unversionedIds = rows.map(r => r.id);
    // drawn before the open from this same entry: its rows are in already
    if (!opts.rowsDrawn && stored.rows) this.drawStored(subscription, stored.held, stored.rows);
    this.moveCursor(subId, stored.held.cursor, 'store');
    if (island!.gen) {
      const buffered = this.loadBuffers.get(subId);
      if (buffered) this.loadBuffers.set(subId, buffered.filter(p => !p.branch || p.branch === island!.lane));
    }
  }

  /** A stored island onto the page: its base's rows as a load applies its
   *  state, then its list, each patch as it was applied (its filters ran
   *  then: a pin's rows, another branch's, an announce). Never recorded
   *  again: a draw runs before the load starts recording, and a load starts
   *  its recording after this (the list is in the store already). */
  private drawStored(subscription: DAGSubscription, held: HeldIsland, rows: BaseRows): void {
    const subId = subscription.subscription_id;
    this.handleSyncDAGResponse({
      type: 'sync-dag-response',
      subscription_id: subId,
      context_type: subscription.context_type,
      context_id: subscription.context_id,
      version: subscription.version + 1,
      timestamp: String(held.cursor),
      root_nodes: rows.root_nodes as any,
      nodes: rows.nodes as any,
      relationships: rows.relationships as any,
    });
    // the list is the head's lane: a page drawn before the open still has
    // the branch it ASKED (main, for my draft), and the replay would drop
    // every patch of my draft as another branch's (VC-e2e's browser cell)
    const asked = subscription.branch;
    subscription.branch = held.lane;
    try {
      for (const p of held.ops) this.handleDAGPatch(this.asIslandPatch(p as unknown as DAGPatch, subscription), { duringLoad: true, fromLoad: true, notify: false });
    } finally {
      subscription.branch = asked;
    }
  }

  /** DRAW BEFORE THE OPEN (stage 2; Tom, 2026-10-19): a page's first open
   *  draws the requested island from what this browser kept for the lane the
   *  page would open now, while the open is in flight: its base from the
   *  pack cache, then its list. Only the requested island: its members wait
   *  for the open, which says which coordinates the page uses (V14: never a
   *  pin version or lane it would not open, never two versions of one
   *  record). The lane is the one the open last answered for the lane this
   *  page asks (its bound branch, or main). Not while a branch is bound for
   *  ANOTHER island: the `x-branch` header goes with every open, and what it
   *  does to this one the draw cannot know. */
  private async drawBeforeOpen(
    subscription: DAGSubscription,
    entry: NonNullable<ReturnType<DAGSyncClient['serving']['get']>>,
    current: () => boolean,
  ): Promise<void> {
    if (!DRAW_BEFORE_OPEN || entry.drawn || !subscription.isLoading || subscription.nodes.size > 0) return;
    const island = subscription.context_id;
    const bound = getActiveBranch();
    if (bound && bound.islandId !== island) return;
    try {
      const lane = await this.islandStore.laneFor(island, subscription.branch ?? DEFAULT_LANE);
      if (!lane || !storableLane(lane, userOf(getServingIdentity()))) return;
      const held = await this.islandStore.get(island, lane);
      if (!held || held.format !== STORE_FORMAT || held.build !== CLIENT_BUILD || held.lane !== lane || held.replay > STORE_MAX_CATCHUP_OPS) return;
      const rows = await this.baseRows(held.base);
      if (!rows) return;
      if (!current() || !subscription.isLoading || subscription.nodes.size > 0) return;
      // kept for the confirm only while the open has not answered: a read that
      // lands later must never stand in for a later re-open
      entry.read = held;
      const before = new Set<string>();
      for (const n of [...rows.root_nodes, ...rows.nodes]) if (typeof n.id === 'string' && graphStore.getNode(n.id)) before.add(n.id);
      this.drawStored(subscription, held, rows);
      const added = new Set(Array.from(subscription.nodes.keys()).filter(id => !before.has(id)));
      entry.drawn = { held, added };
      markDrawn(island);
    } catch { /* nothing drawn: the open loads it */ }
  }

  /** A drawn page the open did not confirm: everything the draw showed goes
   *  — the island's rows and links, and the records it added — unless
   *  another subscription holds them (contract: invalidation removes what
   *  was readable before). */
  private clearDrawn(
    subscription: DAGSubscription,
    entry: { drawn?: { held: HeldIsland; added: Set<string> } },
  ): void {
    const drawn = entry.drawn;
    if (!drawn) return;
    entry.drawn = undefined;
    markAnswered(subscription.context_id);
    for (const [key, rel] of Array.from(subscription.relationships)) {
      if (this.heldByAnotherSubscription(subscription, s => s.relationships.has(key))) continue;
      this.removeEdge(subscription.subscription_id, rel.source, rel.target, rel.type);
    }
    for (const id of Array.from(subscription.nodes.keys())) {
      if (!drawn.added.has(id) && !ownContent(subscription)(subscription.nodes.get(id)!)) continue;
      if (!this.heldByAnotherSubscription(subscription, s => s.nodes.has(id))) graphStore.deleteNode(id);
    }
    subscription.nodes.clear();
    subscription.relationships.clear();
    subscription.isLoading = true;
    this.notifySubscriptionUpdate(subscription);
  }

  /** Write the island's state a while after its last change. */
  private scheduleStore(subscriptionId: string): void {
    if (this.disposed) return;
    const timer = this.storeTimers.get(subscriptionId);
    if (timer) clearTimeout(timer);
    this.storeTimers.set(subscriptionId, setTimeout(() => {
      this.storeTimers.delete(subscriptionId);
      void this.storeNow(subscriptionId);
    }, DAGSyncClient.STORE_WRITE_MS));
  }

  /** Every island with a write pending, now (the page is leaving). */
  private storeAll(): void {
    for (const [id, timer] of Array.from(this.storeTimers)) {
      clearTimeout(timer);
      this.storeTimers.delete(id);
      void this.storeNow(id);
    }
  }

  /** Each island's writes run one after another: a head is in before the
   *  list it starts is appended to. */
  private queueWrite(island: string, write: () => Promise<unknown>): Promise<void> {
    const prev = this.storeWrites.get(island) ?? Promise.resolve();
    const next = prev.then(write, write).then(() => undefined, () => undefined);
    this.storeWrites.set(island, next);
    void next.then(() => { if (this.storeWrites.get(island) === next) this.storeWrites.delete(island); });
    return next;
  }

  /** What is kept for an island (one lane, or all) goes, in order with its
   *  other writes: a head written before it never comes back after it. */
  private forgetStored(island: string, lane?: string): Promise<void> {
    return this.queueWrite(island, () => this.islandStore.delete(island, lane));
  }

  /** A re-open's first step: the patches this page applied since the last
   *  write go into the list, at the cursor they reach, before the stored
   *  entry is read back. Frames held by the open are not applied yet (the
   *  load buffer), so the cut is consistent — unless a gap is open, when the
   *  load's catch-up brings them back instead (`applyStored`). */
  private async writeUnwritten(subscriptionId: string): Promise<void> {
    const entry = this.serving.get(subscriptionId);
    const sub = this.subscriptions.get(subscriptionId);
    const log = this.storeLog.get(subscriptionId);
    const cursor = this.catchupCursors.get(subscriptionId);
    if (this.disposed || !entry || !sub || !log || log.patches.length === 0 || cursor === undefined || !entry.lane) return;
    if (entry.at || sub.error || !storableLane(entry.lane, userOf(getServingIdentity()))) return;
    if ((this.gapHeld.get(subscriptionId)?.length ?? 0) > 0 || this.gapCatchUps.has(subscriptionId)) return;
    const timer = this.storeTimers.get(subscriptionId);
    if (timer) { clearTimeout(timer); this.storeTimers.delete(subscriptionId); }
    const batch = log.patches.splice(0);
    const from = log.seq;
    log.seq = from + batch.length;
    const island = sub.context_id;
    const lane = entry.lane;
    await this.queueWrite(island, async () => {
      const kept = await this.islandStore.append(island, lane, from, batch, cursor);
      if (!kept && this.storeLog.get(subscriptionId) === log) this.storeLog.delete(subscriptionId);
    });
  }

  /** What is written for an island: the patches applied since the last
   *  write, after its list — only when nothing is in flight for it (no
   *  load, no buffered or held frame, no gap catch-up), so the list ends at
   *  a consistent cut. A list the store would not extend (no head, out of
   *  step, past the cut-off) stops being recorded until the next cold load. */
  private async storeNow(subscriptionId: string): Promise<void> {
    if (this.disposed) return;
    const entry = this.serving.get(subscriptionId);
    const sub = this.subscriptions.get(subscriptionId);
    const log = this.storeLog.get(subscriptionId);
    const cursor = this.catchupCursors.get(subscriptionId);
    if (!entry || !sub || !log || log.patches.length === 0 || cursor === undefined) return;
    if (entry.at || !entry.subscribed || sub.error || !storableLane(entry.lane, userOf(getServingIdentity()))) return;
    if (entry.opening || sub.isLoading || (this.loadBuffers.get(subscriptionId)?.length ?? 0) > 0
      || (this.gapHeld.get(subscriptionId)?.length ?? 0) > 0 || this.gapCatchUps.has(subscriptionId)) {
      this.scheduleStore(subscriptionId);
      return;
    }
    const batch = log.patches.splice(0);
    const from = log.seq;
    log.seq = from + batch.length;
    const island = sub.context_id;
    const lane = entry.lane;
    this.queueWrite(island, async () => {
      const kept = await this.islandStore.append(island, lane, from, batch, cursor);
      if (!kept && this.storeLog.get(subscriptionId) === log) this.storeLog.delete(subscriptionId);
    });
  }

  private async fetchIsland(base: string, island: OpenIsland, fs: string) {
    const statePath = island.state.base;
    const noBase = {
      fs: '', island: island.island, line: island.line, commit: null, as_of: 0, expires_at: Infinity,
      root: island.island, packs: [],
    };
    // a commit file's URL names the field set it is rendered under: the
    // open's, or a re-asked manifest's own
    const commitFiles = (commits: string[], under: string) =>
      Promise.all(commits.map(c => getOpsFile(fetch, base, commitFilePath(island.island, c, under))));
    const [state, commits, segments, rows] = await Promise.all([
      loadStateFromPacks({
        initial: island.state.manifest ?? (statePath ? undefined : noBase),
        getManifest: async (ask) => {
          if (!statePath) return noBase;
          // a pack that answered 404 is named, and the answer avoids it
          const missing = (ask?.missing ?? []).map(n => `missing=${encodeURIComponent(n)}`).join('&');
          const url = missing ? `${base}${statePath}${statePath.includes('?') ? '&' : '?'}${missing}` : `${base}${statePath}`;
          const r = await fetch(url, { credentials: 'include' });
          if (!r.ok) throw new Error(`manifest failed (${r.status})`);
          return r.json();
        },
        cache: this.packCache,
      }),
      commitFiles(island.state.commits_after_base, fs),
      Promise.all(island.state.draft_segments.map(path => getOpsFile(fetch, base, path))),
      postUnversioned(fetch, base, island.island, island.unversioned ?? []).catch((): UnversionedRow[] => []),
    ] as const);
    // a manifest re-asked after an expiry may name a NEWER base than the
    // open did (the writer stored one meanwhile): its own list then says
    // which commits come after it (wire §2.1, v1.9)
    const listed = state.commits_after_base;
    const sameList = !listed || (listed.length === island.state.commits_after_base.length
      && listed.every((c, k) => c === island.state.commits_after_base[k]));
    const files = sameList ? commits : await commitFiles(listed!, state.fs || fs);
    return [state, [...files, ...this.segmentsToApply(island, segments, fs)], rows] as const;
  }

  /** The draft's segments to apply after the base: those held for the same
   *  gen, when the open named only the range after them, then the new ones.
   *  Remembers what is held now for the next open. */
  private segmentsToApply(island: OpenIsland, fetched: OpsFile[], fs: string): OpsFile[] {
    const key = this.heldKey(island.island, island.lane);
    const held = this.heldSegments.get(key);
    const range = (url: string | undefined) => {
      const m = url ? /\/(\d+)-(\d+)\.json(?:\?|$)/.exec(url) : null;
      return m ? { from: Number(m[1]), to: Number(m[2]) } : null;
    };
    const urls = island.state.draft_segments;
    if (!island.gen || urls.length === 0) {
      this.heldSegments.delete(key);
      return fetched;
    }
    const first = range(urls[0]);
    const delta = !!first && first.from > 0;
    // the epoch the answer's segments are relative to (a draft's is its gen)
    const epoch = island.segment_epoch ?? undefined;
    const continues = delta && !!held && held.gen === island.gen && held.epoch === epoch
      && held.fs === fs && first!.from === held.to;
    if (delta && !continues) {
      // the open answered the ops after a segment this client cannot use
      // (rendered under another field set or epoch — a seal since — or no
      // longer held): drop it and open again at once, asking for the whole
      // draft. Never laid over the new base: its ops are in it already.
      this.heldSegments.delete(key);
      throw new OpenFailed('held segments do not continue', 410);
    }
    const files = continues ? [...held!.files, ...fetched] : fetched;
    const last = range(urls[urls.length - 1]);
    this.heldSegments.delete(key);
    if (last) this.heldSegments.set(key, { gen: island.gen, ...(epoch !== undefined ? { epoch } : {}), fs, to: last.to, files });
    while (this.heldSegments.size > DAGSyncClient.HELD_SEGMENTS_MAX) {
      this.heldSegments.delete(this.heldSegments.keys().next().value as string);
    }
    return files;
  }

  private applyIsland(t: ServingTarget, [state, files, rows]: Awaited<ReturnType<DAGSyncClient['fetchIsland']>>, cursor: number): void {
    const { subscription, island } = t;
    const subId = subscription.subscription_id;
    // a load replaces the subscription's state: what was applied before it
    // is not "already applied" (as `applyStored`)
    this.seenOps.delete(subId);
    this.applyUnversioned(rows);
    t.entry.unversionedIds = rows.map(r => r.id);
    this.handleSyncDAGResponse({
      type: 'sync-dag-response',
      subscription_id: subId,
      context_type: subscription.context_type,
      context_id: subscription.context_id,
      version: subscription.version + 1,
      timestamp: String(state.as_of),
      root_nodes: state.root_nodes as any,
      nodes: state.nodes as any,
      relationships: state.relationships as any,
    });
    for (const file of files) {
      for (const p of file.patches ?? []) this.handleDAGPatch(this.asIslandPatch(p as DAGPatch, subscription), { duringLoad: true, fromLoad: true });
    }
    this.moveCursor(subId, cursor, 'open');
    // a gen-bound draft's view is its base commit plus its own ops (§2.4):
    // a base-lane frame buffered before the bound took effect is not in it
    if (island!.gen) {
      const buffered = this.loadBuffers.get(subId);
      if (buffered) this.loadBuffers.set(subId, buffered.filter(p => !p.branch || p.branch === island!.lane));
    }
  }

  /** The ENRICHMENT CHANNELS after a load: opened from the base the open
   *  names; on a re-open of the same lane, caught up from their cursors (a
   *  draft's subscriber gets no freshness frames for main's claims — they
   *  come through since, vc-impl 2026-10-09); on a re-open that answers
   *  another lane (a draft landed), opened again from the new base. */
  private openComputed(base: string, t: ServingTarget): void {
    const computed = t.island?.computed;
    if (!computed) return;
    if (t.entry.computed && t.entry.computedSince === computed.since) {
      t.entry.computed.catchUp().catch(() => {});
      return;
    }
    t.entry.computedSince = computed.since;
    // another lane's channel this one replaces: retired, so its late answers
    // are not laid over this lane's
    t.entry.computed?.retire();
    const subId = t.subscription.subscription_id;
    const channel: ComputedChannel = new ComputedChannel({
      subscriptionId: subId,
      apiBase: base,
      baseUrl: computed.base,
      sinceUrl: (c) => `${computed.since}&cursor=${c}`,
      sink: this,
      // as for claims: URLs refused for good (the reader's access changed, or
      // what they name is no longer served) — drop the channel and open the
      // island afresh for new ones. Without it the channel stayed stopped: a
      // re-open of the same lane reused it, and its catch-up had no cursor.
      onAccessChanged: () => {
        invalidateReviewAccess();
        void this.forgetStored(t.subscription.context_id);
        if (t.entry.computed === channel) {
          t.entry.computed = undefined;
          t.entry.computedSince = undefined;
        }
        this.reopenServing(subId);
      },
    });
    t.entry.computed = channel;
    channel.open().catch(() => { if (t.entry.computed === channel) t.entry.computed = undefined; });
  }

  /** A draft's own edit gets no claims freshness frame (draft lanes are
   *  judged through since): catch the claims up CLAIMS_AFTER_EDIT_MS after
   *  the last op frame of the draft's own lane. */
  private claimsAfterDraftEdit(subscriptionId: string, patch: DAGPatch, opts: { fromCatchUp?: boolean; fromLoad?: boolean }): void {
    const entry = this.serving.get(subscriptionId);
    if (!entry?.claims || opts.fromLoad || opts.fromCatchUp) return;
    if (!/^(wip|proposal)\//.test(entry.lane) || patch.branch !== entry.lane || opsOf(patch).length === 0) return;
    if (entry.claimsTimer) clearTimeout(entry.claimsTimer);
    entry.claimsTimer = setTimeout(() => {
      entry.claimsTimer = undefined;
      if (!this.disposed && this.serving.get(subscriptionId) === entry) entry.claims?.catchUp().catch(() => {});
    }, DAGSyncClient.CLAIMS_AFTER_EDIT_MS);
  }

  /** The claims channel (wire §4), from the base the open names; its items
   *  are whole claim records, written to the store as their class. */
  private openClaims(base: string, t: ServingTarget): void {
    const claims = t.island?.claims;
    if (!claims) return;
    if (t.entry.claims && t.entry.claimsSince === claims.since) {
      t.entry.claims.catchUp().catch(() => {});
      return;
    }
    t.entry.claimsSince = claims.since;
    // another lane's channel this one replaces (a draft landed or was
    // discarded): retired at once, and once this lane's base is in, a claim
    // only the old lane had is removed
    const previous = t.entry.claims;
    previous?.retire();
    const subId = t.subscription.subscription_id;
    const channel: ClaimsChannel = new ClaimsChannel({
      subscriptionId: subId,
      apiBase: base,
      baseUrl: claims.base,
      sinceUrl: (c) => `${claims.since}&cursor=${c}`,
      sink: { applyComputed: (_s, items) => this.applyClaims(items), dropComputed: (ids) => this.dropClaims(ids) },
      // the subscription's choice, else the class's default (the manifest's
      // `claims[<class>].send_stale`; a class that does not say keeps them,
      // marked `fresh: false`, as before)
      sendsStale: (label) => {
        const rule = t.subscription.claimsStale;
        if (rule) return rule === 'send';
        return versioningManifest.claims?.[label ?? '']?.send_stale !== false;
      },
      // the claims URLs name the reader and an access epoch (Tom,
      // 2026-10-11) and the stale rule: when access changed, or the rule
      // they name is no longer served (a class default changed), they are
      // stale for good — drop the channel and open the island afresh, which
      // hands out new ones
      onAccessChanged: () => {
        invalidateReviewAccess();
        void this.forgetStored(t.subscription.context_id);
        if (t.entry.claims === channel) {
          t.entry.claims = undefined;
          t.entry.claimsSince = undefined;
        }
        this.reopenServing(subId);
      },
    });
    t.entry.claims = channel;
    channel.open().then(
      () => {
        if (!previous || t.entry.claims !== channel) return;
        const gone = previous.heldIds().filter((id) => !channel.holds(id));
        if (gone.length) this.dropClaims(gone);
      },
      () => { if (t.entry.claims === channel) t.entry.claims = undefined; },
    );
  }

  /** Claim records into the store, newer `_v` winning (an equal `_v` is the
   *  same write: a slim freshness item merges into the record it names). */
  private applyClaims(items: { id: string; _label?: string; _v?: number | null; [k: string]: unknown }[]): void {
    for (const item of items) {
      const held = graphStore.getNode(item.id) as any;
      if (held && typeof held._v === 'number' && typeof item._v === 'number' && item._v < held._v) continue;
      const merged = { ...(held ?? {}), ...item };
      const ModelClass = (ModelRegistry as any)[merged._label ?? ''];
      let instance: any = merged;
      try {
        if (ModelClass?.load) instance = ModelClass.load(merged);
      } catch { /* keep the record, as the patch path does */ }
      graphStore.updateNode(instance);
    }
  }

  private dropClaims(ids: string[]): void {
    for (const id of ids) graphStore.deleteNode(id);
  }

  /** A slim freshness frame (`claims: true`): to the claims channel of each
   *  subscription of that island on the lane it was judged for. */
  private onClaimsFrame(frame: { context_id?: string; branch?: string; items?: unknown[] }): void {
    for (const [id, entry] of Array.from(this.serving)) {
      const sub = this.subscriptions.get(id);
      if (!entry.claims || sub?.context_id !== frame.context_id) continue;
      if (frame.branch && frame.branch !== entry.lane) continue;
      entry.claims.onFrame(frame);
    }
  }


  /** The patch without its changes to rows of an island this page reads AT
   *  a pin (a row's `_island`, held or in the change): only that island's
   *  own load writes them. */
  private withoutPinnedRows(patch: DAGPatch): DAGPatch {
    const pinnedRow = (id: string, props?: Record<string, unknown> | null): boolean => {
      const island = (props?._island as string | undefined) ?? (graphStore.getNode(id) as { _island?: string } | undefined)?._island;
      return !!island && !!pinnedIsland(island);
    };
    const nodes = patch.changes.nodes.filter(n => !pinnedRow(n.node_id, n.properties));
    const relationships = patch.changes.relationships.filter(r => !pinnedRow(r.source_id));
    if (nodes.length === patch.changes.nodes.length && relationships.length === patch.changes.relationships.length) return patch;
    return { ...patch, changes: { ...patch.changes, nodes, relationships } };
  }

  /** An island the server added to `openerId`'s open: a subscription of its
   *  own, held for as long as some opener holds it (or a page subscribes to
   *  it directly). A page's own subscription of it loads itself. */
  private memberFor(openerId: string, island: OpenIsland): ServingTarget | null {
    const subId = `${island.type}:${island.island}`;
    if (subId === openerId) return null;
    let openers = this.memberOf.get(subId);
    if (!openers) this.memberOf.set(subId, (openers = new Set()));
    openers.add(openerId);
    let members = this.membersOf.get(openerId);
    if (!members) this.membersOf.set(openerId, (members = new Set()));
    members.add(subId);
    const existing = this.subscriptions.get(subId);
    const held = this.serving.get(subId);
    if (existing && !(held?.groupOf)) {
      // a page's own subscription — unless its last view has left and it only
      // waits out the unsync debounce: this opener then adopts it (its rows
      // are read again at the opener's coordinate, and it is not released
      // from under the opener)
      const pending = this.pendingUnsyncs.get(subId);
      if (!pending || (this.subscriptionRefCounts.get(subId) ?? 0) > 0) return null;
      clearTimeout(pending);
      this.pendingUnsyncs.delete(subId);
      this.subscriptionRefCounts.delete(subId);
      if (held) held.groupOf = openerId;
    }
    if (!existing) {
      this.subscriptions.set(subId, {
        subscription_id: subId, context_type: island.type, context_id: island.island, branch: island.lane,
        nodes: new Map(), relationships: new Map(), version: 0, timestamp: new Date().toISOString(), isLoading: true,
      });
    }
    const entry = held ?? { gen: null, fs: '', lane: island.lane, subscribed: false, groupOf: openerId };
    if (island.spec !== undefined) (entry as { spec?: unknown }).spec = island.spec;
    this.serving.set(subId, entry);
    if (!this.loadBuffers.has(subId)) this.loadBuffers.set(subId, []);
    return { subscription: this.subscriptions.get(subId)!, entry, island };
  }

  /** An opener's subscription ended: the islands only it held go too
   *  (and theirs, two deep). */
  private releaseMembers(openerId: string): void {
    const members = this.membersOf.get(openerId);
    this.membersOf.delete(openerId);
    for (const m of Array.from(members ?? [])) {
      const openers = this.memberOf.get(m);
      openers?.delete(openerId);
      if (openers?.size || (this.subscriptionRefCounts.get(m) ?? 0) > 0) continue;
      this.memberOf.delete(m);
      const sub = this.subscriptions.get(m);
      if (sub && this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ type: 'unsync-dag', context_type: sub.context_type, context_id: sub.context_id }));
      }
      this.releaseOwnRows(sub);
      if (sub && this.serving.get(m)?.at) forgetPinnedIsland(sub.context_id);
      this.subscriptions.delete(m);
      this.forgetHeldFor(m);
      this.loadBuffers.delete(m);
      this.releaseMembers(m);
    }
  }

  /** An op frame's `open` (wire §1, islands added later): a new sub-task, a
   *  newly referenced item — opened for the frame's group. */
  private async openMore(
    subscriptionId: string,
    wanted: { type: string; id: string; at?: string; spec?: unknown }[],
    opts: { again?: string; attempt?: number; reopened410?: boolean } = {},
  ): Promise<void> {
    const base = this.restBase();
    const rootId = this.serving.get(subscriptionId)?.groupOf ?? subscriptionId;
    // a frame's `open` names islands not held yet; a member's re-open
    // (`again`) names one that is
    const fresh = opts.again ? wanted : wanted.filter(w => !this.subscriptions.has(`${w.type}:${w.id}`));
    if (!base || !fresh.length || this.disposed) return;
    const member = opts.again ? this.serving.get(opts.again) : undefined;
    if (member) {
      member.opening = true;
      member.again = false;
      if (!this.loadBuffers.has(opts.again!)) this.loadBuffers.set(opts.again!, []);
    }
    let targets: ServingTarget[] = [];
    // the 410 retry below takes over this attempt: `opening` and a re-open
    // that waited for it are then the retry's to finish, not this finally's
    let handedOver = false;
    try {
      const answer = await postOpen(fetch, base, {
        islands: fresh.map(w => ({
          type: w.type, id: w.id, ...(w.at ? { at: w.at } : {}), ...(w.spec !== undefined ? { spec: w.spec } : {}),
          ...this.lastSegmentFor(w.id, this.subscriptions.get(`${w.type}:${w.id}`)?.branch),
          ...this.claimsChoice(this.subscriptions.get(`${w.type}:${w.id}`) ?? this.subscriptions.get(rootId)),
        })),
        channels: ['state', 'computed', 'claims', 'unversioned'],
      }, branchHeaders());
      if (this.disposed || !this.subscriptions.has(rootId)) return;
      targets = answer.islands.map(i => this.memberFor(rootId, i)).filter((t): t is ServingTarget => !!t);
      this.retryMemberGenesis(subscriptionId, rootId, fresh, answer.refused ?? [], opts);
      await this.loadTargets(base, targets, answer);
    } catch (e) {
      if (e instanceof OpenFailed && e.status === 410 && !opts.reopened410) {
        // as for an open: once more at once, under the current field set
        handedOver = true;
        void this.openMore(subscriptionId, wanted, { ...opts, reopened410: true });
        return;
      }
      // as for an open: again after a pause, then the islands err
      const attempt = (opts.attempt ?? 0) + 1;
      const wait = DAGSyncClient.OPEN_RETRY_MS[attempt - 1];
      if (wait !== undefined) {
        setTimeout(() => {
          if (!this.disposed && this.subscriptions.has(rootId)) void this.openMore(subscriptionId, wanted, { ...opts, attempt });
        }, wait);
      } else {
        for (const t of targets) this.failServing(t.subscription, 'state unavailable', 503);
      }
    } finally {
      if (member && !handedOver) {
        member.opening = false;
        if (member.again && this.serving.get(opts.again!) === member) this.reopenServing(opts.again!);
      }
    }
  }

  /** A patch answered for ONE island (a catch-up, a segment, a commit
   *  file) belongs to that island's subscription, whatever type it names:
   *  the server's renderer named every island "Project" (a ChatTask's
   *  segment, VC-D's fixture v3), and a patch is routed by type and id. */
  private asIslandPatch(patch: DAGPatch, subscription: DAGSubscription): DAGPatch {
    if (patch.context_id !== subscription.context_id || patch.context_type === subscription.context_type) return patch;
    return { ...patch, context_type: subscription.context_type };
  }

  private sendSubscribe(subscription: DAGSubscription, gen: string | null): void {
    const entry = this.serving.get(subscription.subscription_id);
    if (entry) entry.acked = false;
    const message: WebSocketMessage = {
      type: 'sync-dag',
      subscription_id: subscription.subscription_id,
      context_type: subscription.context_type,
      context_id: subscription.context_id,
      branch: subscription.branch,
      light: true,
      ...(gen ? { gen } : {}),
    };
    if (this.websocket?.readyState === WebSocket.OPEN) this.websocket.send(JSON.stringify(message));
  }

  /** A refusal: re-ask a genesis still pending, else the subscription errs
   *  as a socket refusal does (a 404 consumer retries the create race).
   *  Answers whether the load continues (the buffer is kept). */
  private refuseServing(
    subscription: DAGSubscription,
    entry: { retryTimer?: ReturnType<typeof setTimeout>; genesisSince?: number },
    refusal: OpenRefusal,
    opts: { reopen?: boolean },
  ): boolean {
    const retry = genesisRetryMs(refusal);
    if (retry !== null) {
      const now = Date.now();
      entry.genesisSince ??= now;
      if (now - entry.genesisSince + retry <= GENESIS_BUDGET_MS) {
        entry.retryTimer = setTimeout(() => {
          entry.retryTimer = undefined;
          if (this.subscriptions.get(subscription.subscription_id) === subscription) void this.openServing(subscription, opts);
        }, retry);
        return true;
      }
    }
    this.failServing(subscription, refusalText(refusal), refusal.code, refusalDetail(refusal));
    return false;
  }

  /** The subscribe was acknowledged: frames flow from here. If the load
   *  caught up before it, an op published in between is fetched now. */
  private onServingAck(subscriptionId: string): void {
    const entry = this.serving.get(subscriptionId);
    const sub = this.subscriptions.get(subscriptionId);
    if (!entry || !sub) return;
    entry.acked = true;
    if (entry.caughtUpUnacked) {
      entry.caughtUpUnacked = false;
      void this.catchUp(sub.context_id, subscriptionId, { quiet: true });
    }
  }

  /** An island the open EXPANDS to was refused genesis_pending (its first
   *  commit is still being made): it would be silently absent. The group is
   *  opened again after its retry_after_ms — the refusal names no type to
   *  open it alone — for as long as GENESIS_BUDGET_MS allows. */
  private retryExpandedGenesis(subscription: DAGSubscription, entry: NonNullable<ReturnType<DAGSyncClient['serving']['get']>>, refused: OpenRefusal[]): void {
    const waits = refused
      .filter((r) => r.island !== subscription.context_id)
      .map((r) => genesisRetryMs(r))
      .filter((ms): ms is number => ms !== null);
    if (!waits.length) { entry.expandedGenesisSince = undefined; return; }
    if (entry.expandedRetryTimer) return;
    const now = Date.now();
    entry.expandedGenesisSince ??= now;
    const wait = Math.min(...waits);
    if (now - entry.expandedGenesisSince + wait > GENESIS_BUDGET_MS) return;
    const subId = subscription.subscription_id;
    entry.expandedRetryTimer = setTimeout(() => {
      entry.expandedRetryTimer = undefined;
      if (!this.disposed && this.serving.get(subId) === entry && this.subscriptions.get(subId) === subscription) this.reopenServing(subId);
    }, wait);
  }

  /** An expanded open (`openMore`) whose named island was refused
   *  genesis_pending: open that one again after its retry_after_ms, within
   *  GENESIS_BUDGET_MS. */
  private memberGenesisSince = new Map<string, number>();
  private memberGenesisTimers = new Set<ReturnType<typeof setTimeout>>();
  private retryMemberGenesis(
    subscriptionId: string,
    rootId: string,
    wanted: { type: string; id: string; at?: string; spec?: unknown }[],
    refused: OpenRefusal[],
    opts: { again?: string; attempt?: number; reopened410?: boolean },
  ): void {
    for (const w of wanted) {
      const key = `${rootId}|${w.id}`;
      const r = refused.find((x) => x.island === w.id);
      const wait = r ? genesisRetryMs(r) : null;
      if (wait === null) { this.memberGenesisSince.delete(key); continue; }
      const now = Date.now();
      const since = this.memberGenesisSince.get(key) ?? now;
      this.memberGenesisSince.set(key, since);
      if (now - since + wait > GENESIS_BUDGET_MS) continue;
      const timer = setTimeout(() => {
        this.memberGenesisTimers.delete(timer);
        if (!this.disposed && this.subscriptions.has(rootId)) void this.openMore(subscriptionId, [w], opts);
      }, wait);
      this.memberGenesisTimers.add(timer);
    }
  }

  private failServing(subscription: DAGSubscription, text: string, code: number, detail?: RefusalDetail): void {
    this.serving.delete(subscription.subscription_id);
    this.loadBuffers.delete(subscription.subscription_id);
    subscription.error = text;
    subscription.errorCode = code as any;
    subscription.errorDetail = detail;
    subscription.isLoading = false;
    this.notifySubscriptionUpdate(subscription);
  }

  /** Re-open the state channel (a new gen, a 410, another field set). The
   *  enrichment channels keep their cursors (wire §2.4). */
  private reopenServing(subscriptionId: string): void {
    const entry = this.serving.get(subscriptionId);
    const subscription = this.subscriptions.get(subscriptionId);
    if (!entry || !subscription || this.disposed) return;
    // a pinned island opened alone would come at head: its opener is opened
    // again, and answers it at the opener's pin
    if (entry.at && entry.groupOf && this.subscriptions.has(entry.groupOf)) {
      this.reopenServing(entry.groupOf);
      return;
    }
    if (entry.groupOf && this.subscriptions.has(entry.groupOf)) {
      // an expanded island re-opens ALONE, held by its group: a chat
      // sub-task's gen moves on every commit (wire §2.4, v1.9), and
      // re-opening the whole group each turn would reload every island
      if (entry.opening) { entry.again = true; return; }
      void this.openMore(entry.groupOf, [{
        type: subscription.context_type, id: subscription.context_id,
        ...(entry.spec !== undefined ? { spec: entry.spec } : {}),
      }], { again: subscriptionId });
      return;
    }
    if (entry.opening) { entry.again = true; return; }
    void this.openServing(subscription, { reopen: true });
  }

  /** `dag-subscription-revoked` (410 `access_changed`): the server stopped
   *  sending this subscription frames because the reader's access changed.
   *  The island is opened again, as set 1 answers access_changed: the open
   *  answers what the reader may now see, or refuses and the view says why. */
  private onSubscriptionRevoked(frame: { context_type?: string; context_id?: string; reason?: string; status?: number }) {
    if (!frame.context_type || !frame.context_id) return;
    invalidateReviewAccess();
    void this.forgetStored(frame.context_id);
    const subscriptionId = `${frame.context_type}:${frame.context_id}`;
    const entry = this.serving.get(subscriptionId);
    if (entry) {
      entry.subscribed = false;
      this.reopenServing(subscriptionId);
      return;
    }
    const sub = this.subscriptions.get(subscriptionId);
    if (!sub) return;
    sub.error = frame.reason ?? 'Your access changed.';
    sub.errorCode = typeof frame.status === 'number' ? frame.status : 410;
    sub.isLoading = false;
    this.notifySubscriptionUpdate(sub);
  }

  /** `dag-gen-superseded` (wire §3.1): the socket has stopped sending this
   *  subscription frames until it re-subscribes with the current gen. */
  private onGenSuperseded(frame: { context_type: string; context_id: string; gen: string }): void {
    const subscriptionId = `${frame.context_type}:${frame.context_id}`;
    const entry = this.serving.get(subscriptionId);
    if (!entry || entry.gen !== frame.gen) return;
    void this.forgetStored(frame.context_id, entry.lane);
    // the server has DROPPED this subscription: it sends it nothing more
    // until it subscribes again — so the re-open must re-subscribe even when
    // its answer names the same gen (Tom's stack, 2026-10-14: the open kept
    // answering the superseded gen, no subscribe went out, and the chat
    // moved only by catch-up every ~40 s)
    entry.subscribed = false;
    const again = entry.supersededGen === frame.gen;
    entry.supersededGen = frame.gen;
    if (!again) {
      entry.supersededTries = 0;
      this.reopenServing(subscriptionId);
      return;
    }
    // the same gen superseded again after a re-open: the open and the socket
    // disagree on the current gen. Re-open with backoff — the catch-up keeps
    // the page moving meanwhile — never at every frame.
    if (entry.supersededTimer) return;
    const tries = entry.supersededTries ?? 0;
    const wait = DAGSyncClient.SUPERSEDED_RETRY_MS[Math.min(tries, DAGSyncClient.SUPERSEDED_RETRY_MS.length - 1)];
    entry.supersededTries = tries + 1;
    entry.supersededTimer = setTimeout(() => {
      entry.supersededTimer = undefined;
      if (!this.disposed && this.serving.get(subscriptionId) === entry) this.reopenServing(subscriptionId);
    }, wait);
  }
  static SUPERSEDED_RETRY_MS = [1_000, 3_000, 10_000, 30_000];

  /** Unversioned rows (§5) into the graph store, newer `_v` winning. Not a
   *  subscription's own content: a re-load's removal never touches them. */
  private applyUnversioned(rows: UnversionedRow[]): void {
    for (const row of rows) {
      const v = typeof row._v === 'number' ? row._v : -Infinity;
      const held = this.unversioned.get(row.id);
      if (held !== undefined && held > v) continue;
      this.unversioned.set(row.id, v);
      const ModelClass = (ModelRegistry as any)[row._label ?? ''];
      let instance: any = row;
      try {
        if (ModelClass?.load) instance = ModelClass.load(row);
      } catch { /* keep the row, as the patch path does */ }
      graphStore.updateNode(instance);
    }
  }

  private reloadUnversioned(island: string, ids: string[]): void {
    const base = this.restBase();
    if (!base) return;
    postUnversioned(fetch, base, island, ids).then(rows => { if (!this.disposed) this.applyUnversioned(rows); }).catch(() => {});
  }


  /**
   * OP ORDER for a frame that carries ops (VC-D's wire: each op has `op_id`,
   * `ts`, `depends_on` and `depends_on_ts` {op_id: ts}; a live frame has
   * `safe_cursor`). Answers whether to apply the frame now.
   * - A frame every op of which is already reflected — applied, or stamped
   *   at or before my cursor — is a DUPLICATE (a catch-up overlapping live
   *   delivery, a held frame its own gap catch-up brought): dropped.
   * - A live frame with a dependency stamped after my cursor that I have not
   *   applied is HELD, and a catch-up fetches the gap at once — applied
   *   first, an update would land before its create or an edge before its
   *   end. A dependency at or before my cursor is in the state; one the
   *   server could not date (absent from `depends_on_ts`) counts as covered.
   * - Otherwise applied, and when no gap is open my cursor moves to the
   *   frame's `safe_cursor`: every op stamped at or before it is in my state,
   *   delivered to me, or returned by a catch-up from below it. Moving it
   *   while a gap is open would skip the gap for good.
   * A frame with no ops (computed values, announces) is not ordered.
   */
  private admitOps(
    subscriptionId: string,
    patch: DAGPatch,
    opts: { fromCatchUp?: boolean; released?: boolean; fromLoad?: boolean },
  ): boolean {
    const ops = opsOf(patch);
    this.armQuietCatchUp(patch.context_id, subscriptionId);
    if (ops.length === 0) return true;
    const seen = this.seenFor(subscriptionId);
    if (opts.fromLoad) {
      // a segment or commit file of the load itself (wire §2.2, §2.4): part
      // of the state, stamped at or before the open's cursor, never judged
      for (const op of ops) if (op.op_id) seen.set(op.op_id, typeof op.ts === 'number' ? op.ts : NaN);
      return true;
    }
    // already reflected: applied (seen), or stamped at or before my cursor —
    // the cursor is a watermark, so such an op is in my state, was applied
    // from an earlier frame, or came back in the catch-up that moved the
    // cursor (a held frame released after its gap catch-up is one of these;
    // re-applied, it would undo a newer op that catch-up applied)
    const cursorNow = this.catchupCursors.get(subscriptionId);
    const reflected = (op: WireOp) =>
      (!!op.op_id && seen.has(op.op_id)) ||
      (typeof op.ts === 'number' && cursorNow !== undefined && op.ts <= cursorNow);
    if (ops.every(reflected)) {
      for (const op of ops) {
        this.noteDropped(subscriptionId, op, cursorNow, !!op.op_id && seen.has(op.op_id) ? "already applied" : "stamped at or before the cursor");
      }
      return false;
    }
    if (!opts.fromCatchUp && !opts.released && this.hasMissingDependency(subscriptionId, ops)) {
      const held = this.gapHeld.get(subscriptionId) ?? [];
      held.push(patch);
      this.gapHeld.set(subscriptionId, held);
      this.catchUpForGap(patch.context_id, subscriptionId);
      return false;
    }
    for (const op of ops) {
      if (op.op_id) seen.set(op.op_id, typeof op.ts === 'number' ? op.ts : NaN);
    }
    const safe = (patch as { safe_cursor?: unknown }).safe_cursor;
    const cursor = this.catchupCursors.get(subscriptionId);
    if (
      !opts.fromCatchUp &&
      typeof safe === 'number' &&
      cursor !== undefined && safe > cursor &&
      !this.gapHeld.get(subscriptionId)?.length &&
      !this.gapCatchUps.has(subscriptionId)
    ) {
      this.moveCursor(subscriptionId, safe, "safe_cursor", ops.map((op) => op.op_id).filter((x): x is string => !!x));
      this.pruneSeen(subscriptionId);
    }
    return true;
  }

  private hasMissingDependency(subscriptionId: string, ops: WireOp[]): boolean {
    const cursor = this.catchupCursors.get(subscriptionId);
    if (cursor === undefined) return false; // no cursor to catch up from
    const seen = this.seenFor(subscriptionId);
    const inFrame = new Set(ops.map(op => op.op_id).filter(Boolean));
    return ops.some(op =>
      (op.depends_on ?? []).some(dep => {
        if (inFrame.has(dep) || seen.has(dep)) return false;
        const ts = op.depends_on_ts?.[dep];
        return typeof ts === 'number' && ts > cursor;
      })
    );
  }

  /** Fetch a gap now, then apply the frames held for it, in arrival order.
   *  One catch-up at a time per subscription; a frame held while it runs
   *  waits for it. Should the catch-up fail or not return the dependency,
   *  the held frames are applied anyway: a late op beats a lost one. */
  private catchUpForGap(contextId: string, subscriptionId: string): void {
    if (this.gapCatchUps.has(subscriptionId)) return;
    const run = (async () => {
      try {
        await this.catchUp(contextId, subscriptionId);
      } finally {
        this.gapCatchUps.delete(subscriptionId);
        const held = this.gapHeld.get(subscriptionId) ?? [];
        this.gapHeld.delete(subscriptionId);
        for (const p of held) this.handleDAGPatch(p, { notify: false, released: true });
      }
    })();
    this.gapCatchUps.set(subscriptionId, run);
  }

  /** (Re)start the quiet-interval catch-up: a lost publish is otherwise
   *  noticed only at the next reconnect or reload. Any frame or catch-up
   *  postpones it; only a subscription with a cursor has one. */
  private armQuietCatchUp(contextId: string, subscriptionId: string): void {
    if (!this.catchupCursors.has(subscriptionId) || !this.subscriptions.has(subscriptionId)) return;
    const prior = this.quietTimers.get(subscriptionId);
    if (prior) clearTimeout(prior);
    const wait = DAGSyncClient.QUIET_MS * (0.8 + 0.4 * Math.random());
    if (this.disposed) return;
    const timer = setTimeout(() => {
      this.quietTimers.delete(subscriptionId);
      if (!this.subscriptions.has(subscriptionId) || this.loadBuffers.has(subscriptionId)) return;
      void this.catchUp(contextId, subscriptionId, { quiet: true }); // re-arms when it ends
    }, wait);
    (timer as { unref?: () => void }).unref?.();
    this.quietTimers.set(subscriptionId, timer);
  }

  private seenFor(subscriptionId: string): Map<string, number> {
    let seen = this.seenOps.get(subscriptionId);
    if (!seen) this.seenOps.set(subscriptionId, (seen = new Map()));
    return seen;
  }

  /** Forget the ops at or before the cursor (the state covers them), and
   *  the oldest beyond the cap. */
  private pruneSeen(subscriptionId: string): void {
    const seen = this.seenOps.get(subscriptionId);
    const cursor = this.catchupCursors.get(subscriptionId);
    if (!seen || cursor === undefined) return;
    for (const [id, ts] of Array.from(seen)) {
      if (ts <= cursor) seen.delete(id);
    }
    const excess = seen.size - DAGSyncClient.SEEN_CAP;
    if (excess > 0) {
      for (const id of Array.from(seen.keys()).slice(0, excess)) seen.delete(id);
    }
  }

  /** The load is in: apply the live patches that arrived during it, in the
   *  order they arrived, SKIPPING those the load already reflects — every op
   *  of the patch either stamped at or before the load's cursor (in the
   *  state) or replayed by the load's catch-up. Re-applying one would put an
   *  older value back over a newer one the catch-up applied after it. A
   *  patch carrying no ops (a computed-value patch) is applied: the latest. */
  private flushLoadBuffer(subscriptionId: string, loadCursor?: number, reflected?: Set<string> | null): void {
    const buffered = this.loadBuffers.get(subscriptionId);
    this.loadBuffers.delete(subscriptionId);
    if (!buffered) return;
    for (const patch of buffered) {
      const ops = (patch as unknown as { ops?: { op_id?: string; ts?: unknown }[] }).ops ?? [];
      const inLoad = ops.length > 0 && ops.every(op =>
        (!!op.op_id && !!reflected?.has(op.op_id)) ||
        (typeof op.ts === 'number' && loadCursor !== undefined && op.ts <= loadCursor)
      );
      if (inLoad) {
        for (const op of ops) {
          this.noteDropped(subscriptionId, op as WireOp, loadCursor,
            !!op.op_id && !!reflected?.has(op.op_id) ? "replayed by the load's catch-up" : "stamped at or before the load's cursor");
        }
        continue;
      }
      this.handleDAGPatch(patch, { duringLoad: true, notify: false });
    }
  }

  /** Seq catch-up (67 §9): replay ops appended since our cursor through the
   *  normal patch path. Runs on reconnect; overlap is idempotent. Answers the
   *  op ids it replayed (null when it could not run). `duringLoad`: it is
   *  the load's own catch-up, so its patches apply now, not into the load
   *  buffer. */
  private async catchUp(
    contextId: string,
    subscriptionId: string,
    opts: { duringLoad?: boolean; quiet?: boolean; byCursor?: boolean; maxOps?: number } = {},
  ): Promise<Set<string> | null> {
    // a pinned island is one commit: nothing to catch up to
    if (this.serving.get(subscriptionId)?.at) return null;
    const base = this.restBase();
    const cursor = this.catchupCursors.get(subscriptionId);
    if (!base || cursor === undefined) return null;
    try {
      // a draft opened through the serving open reads its OWN lane, bound to
      // its gen (wire §2.4); a 410 (gen superseded, or a cursor older than
      // the ops the island keeps) means re-open
      const gen = opts.byCursor ? null : this.serving.get(subscriptionId)?.gen;
      const r = await fetch(`${base}/versioning/${contextId}/ops/since?cursor=${cursor}&branch=${encodeURIComponent(this.laneOf(contextId, subscriptionId))}${gen ? `&gen=${encodeURIComponent(gen)}` : ''}`, { credentials: 'include', headers: branchHeaders() });
      if (r.status === 401) sessionEnded();
      if (r.status === 410) void this.forgetStored(contextId, this.laneOf(contextId, subscriptionId));
      if (r.status === 410 && this.serving.has(subscriptionId)) {
        this.reopenServing(subscriptionId);
        return null;
      }
      if (!r.ok) return null;
      const body = await r.json();
      // a stored island too far behind (STORE_MAX_CATCHUP_OPS): nothing is
      // applied, and the caller loads it from its newest snapshot instead
      if (opts.maxOps !== undefined) {
        const ops = (body.patches ?? []).reduce((n: number, p: { ops?: unknown[] }) => n + (p?.ops?.length ?? 0), 0);
        if (ops > opts.maxOps) return null;
      }
      // REPLAYED = APPLIED: an op counts as replayed (and its buffered live
      // copy as covered) only when it is in my applied set after its patch
      // ran — one this catch-up's patch skipped is left to the live copy
      const replayed = new Set<string>();
      const seen = this.seenFor(subscriptionId);
      const caughtUp = this.subscriptions.get(subscriptionId);
      for (const raw of body.patches ?? []) {
        const patch = caughtUp ? this.asIslandPatch(raw as DAGPatch, caughtUp) : (raw as DAGPatch);
        this.handleDAGPatch(patch, { duringLoad: opts.duringLoad, fromCatchUp: true });
        for (const op of (patch as { ops?: { op_id?: string }[] }).ops ?? []) {
          if (op.op_id && seen.has(op.op_id)) replayed.add(op.op_id);
        }
      }
      if (typeof body.cursor === 'number') {
        // NEVER BACKWARDS: a catch-up's cursor can sit below a safe cursor a
        // live frame already moved me to (each is capped at its own moment)
        const current = this.catchupCursors.get(subscriptionId);
        this.moveCursor(subscriptionId, current === undefined ? body.cursor : Math.max(current, body.cursor), "catch-up");
        this.pruneSeen(subscriptionId);
      }
      this.lastCatchUpAt.set(subscriptionId, Date.now());
      this.armQuietCatchUp(contextId, subscriptionId);
      // the channels catch up when the state's catch-up had a reason to (a
      // gap, a reconnect) or a quiet one found ops: a lost publish may have
      // lost their frames too. A quiet catch-up that found nothing leaves
      // them: their `since` is for a gap, not a loop (the brief; an idle
      // page of 9 islands made ~27 requests per 45 s, VC-e2e 2026-10-13).
      // The load's own catch-up runs before they are opened.
      const found = (body.patches ?? []).length > 0;
      if (!opts.duringLoad && (!opts.quiet || found)) {
        const serving = this.serving.get(subscriptionId);
        serving?.computed?.catchUp().catch(() => {});
        serving?.claims?.catchUp().catch(() => {});
      }
      return replayed;
    } catch {
      return null; /* nothing replayed: what is held is applied as it is */
    }
  }

  /** `duringLoad`: applied by the load itself (its catch-up, or the buffer
   *  flush) — never into the load buffer. `notify: false`: the ops
   *  followers already had this patch when it arrived (a buffered one). */
  private handleDAGPatch(
    patch: DAGPatch,
    opts: { duringLoad?: boolean; notify?: boolean; fromCatchUp?: boolean; released?: boolean; fromLoad?: boolean } = {},
  ): void {
    if (this.disposed) return; // a catch-up of the client before a reconnect
    // op metadata rides the patch (versioning relay); deliver to followers
    // FIRST — an ops-follow listener needs no data-store subscription match
    const rawOps = (patch as unknown as { ops?: unknown[] }).ops;
    const followSub = this.subscriptions.get(`${patch.context_type}:${patch.context_id}`);
    // the island's pending changes moved: asked again, before any follower
    // reloads (one shared pending-changes answer per island)
    if (opts.notify !== false && rawOps && rawOps.length) pendingChangesChanged(patch.context_id);
    if (opts.notify !== false && rawOps && rawOps.length && branchVisible(patch.branch, followSub?.branch)) {
      for (const h of this.opsHandlers) {
        try {
          h({ context_type: patch.context_type, context_id: patch.context_id, ops: rawOps, branch: patch.branch });
        } catch { /* listener errors never break the stream */ }
      }
    }
    // Find subscription by context instead of subscription_id (which is undefined in patches)
    const subscriptionId = `${patch.context_type}:${patch.context_id}`;
    const subscription = this.subscriptions.get(subscriptionId);

    if (!subscription) {
      console.warn('Received dag-patch for unknown subscription context:', subscriptionId, 'Available subscriptions:', Array.from(this.subscriptions.keys()));
      return;
    }

    // a PINNED island takes no frame but its own load's: the page shows
    // the pinned commit until the holder's pin moves (V17)
    if (this.serving.get(subscriptionId)?.at && !opts.fromLoad) return;

    // ...nor on another island's channel: the relay re-sends an item
    // island's changes to every project that references it (relay.py,
    // "implicit-subscription fan-out", 67 §10), its head on the holder's
    // channel. A row of a pinned island keeps its pinned values (V17).
    if (!opts.fromLoad) patch = this.withoutPinnedRows(patch);

    // the holder's pin record changed (a pin staged, updated, committed or
    // removed on this lane): open again, so each island it pins is answered
    // at its new commit (or live, when unpinned). Once per frame: a frame
    // held while a load runs is replayed `duringLoad`, after it was seen.
    if (!opts.fromLoad && !opts.duringLoad && this.serving.has(subscriptionId)
      && patch.changes.nodes.some(n => n.node_type === 'IslandPin')
      && branchVisible(patch.branch, subscription.branch)) {
      this.reopenServing(subscriptionId);
    }

    // Phase C: a patch from a branch this subscription cannot see is
    // dropped (the server filters too; this guards a stale subscription
    // between a branch switch and its re-sync)
    if (!branchVisible(patch.branch, subscription.branch)) {
      return;
    }

    // A frame under another field set (a deploy), or an
    // announce of a new draft generation (a baseline, a rebase), means the
    // state this client loaded is no longer the fold: re-open (wire §3.1,
    // §3.3). An announce of an unversioned row re-reads it (§5).
    const serving = this.serving.get(subscriptionId);
    const frameFs = (patch as { fs?: unknown }).fs;
    const frameOpens = (patch as { open?: unknown }).open;
    if (serving && !opts.fromLoad && Array.isArray(frameOpens) && frameOpens.length) {
      void this.openMore(subscriptionId, frameOpens as { type: string; id: string; at?: string }[]);
    }
    if (serving && serving.fs && typeof frameFs === 'string' && frameFs !== serving.fs && serving.fsSeen !== frameFs) {
      serving.fsSeen = frameFs; // once per field set: a buffered copy replays it
      this.reopenServing(subscriptionId);
    }

    // Record-announce lane (67 §9): announce-marked creations notify
    // consumers and are stripped — the record is fetched, never patched in.
    const announces = patch.changes.nodes.filter(
      n => n.change_type === 'created' && n.properties?._record_announce
    );
    if (announces.length) {
      for (const n of announces) {
        if (serving) {
          // an overlay's announce carries ITS draft's gen: it re-opens only
          // the subscription on that draft's lane (`lane` when the announce
          // names it). Announces go to every subscriber of the island, and a
          // line's gen survives a seal (item 95): before, any draft's
          // baseline or landing re-opened every view of the island.
          const gen = n.properties?.gen;
          const lane = n.properties?.lane;
          // a NEW COMMIT on a line of this island (mine or anyone's): its
          // packs into the pack cache in idle time, for the next cold open.
          // The record's id is the commit's; its line, the announce's lane,
          // else the line this view reads.
          if (n.node_type === 'VersioningCommit' && !serving.at) {
            this.prefetcher.want(subscription.context_id, typeof lane === 'string' ? lane : (serving.line ?? serving.lane), n.node_id);
          }
          const mine = typeof lane === 'string' ? lane === serving.lane : /^(wip|proposal)\//.test(serving.lane);
          if (typeof gen === 'string' && gen !== serving.gen && mine) this.reopenServing(subscriptionId);
          if (this.unversioned.has(n.node_id)) this.reloadUnversioned(subscription.context_id, [n.node_id]);
        }
        // a draft, commit or pin record of the island: its pending changes
        // (branch, count, pins, proposals) are asked again before listeners
        if (n.node_type === 'VersioningOverlay' || n.node_type === 'VersioningCommit' || n.node_type === 'IslandPin') {
          pendingChangesChanged(patch.context_id);
        }
        for (const h of this.recordAnnounceHandlers) {
          try {
            h({
              context_id: patch.context_id,
              node_id: n.node_id,
              node_type: n.node_type,
              pinned_commit_id: n.properties?.pinned_commit_id,
              change: n.properties?._announce_change,
              attached_entity: n.properties?._attached_entity,
              attached_hash: n.properties?._attached_hash,
            });
          } catch { /* consumer errors never break the stream */ }
        }
      }
      patch = {
        ...patch,
        changes: {
          ...patch.changes,
          nodes: patch.changes.nodes.filter(n => !announces.includes(n)),
        },
      };
      if (patch.changes.nodes.length === 0 && patch.changes.relationships.length === 0 && !patch.computed?.length) {
        return;
      }
    }

    // =========================================================================
    // STEP 0: a load of this subscription is in flight — hold the patch until
    // its state and catch-up are in (`flushLoadBuffer`). Applied now, it
    // would be overwritten by the state the load then writes.
    // =========================================================================
    if (!opts.duringLoad) {
      const loadBuffer = this.loadBuffers.get(subscriptionId);
      if (loadBuffer) {
        loadBuffer.push(patch);
        return;
      }
    }

    // =========================================================================
    // STEP 0b: op order — a duplicate is dropped; a frame whose dependency
    // has not arrived is held while a catch-up fetches it
    // =========================================================================
    if (!this.admitOps(subscriptionId, patch, opts)) {
      // its ops are reflected already — a catch-up brought them, and a
      // catch-up carries no computed values — so the edit is in but its
      // stale flags may not be: they are applied (older never over newer).
      // A frame HELD for a gap keeps them for when it is applied.
      if (!this.gapHeld.get(subscriptionId)?.includes(patch)) this.applyFrameComputed(subscriptionId, patch);
      return;
    }

    // THE ISLAND STORE'S LIST (shape B): the patch as it is applied, once —
    // after its filters (a pin's rows, another branch, announces), the load
    // buffer and the op order — so a replay applies exactly what this page
    // did. A copy (what is applied may be changed in place), without its
    // computed values: they come by the computed channel, as on a cold load,
    // and a replayed one would be stale.
    const log = this.storeLog.get(subscriptionId);
    if (log) {
      try {
        const { computed: _computed, ...kept } = JSON.parse(JSON.stringify(patch)) as StoredPatch;
        log.patches.push(kept);
      } catch { this.storeLog.delete(subscriptionId); }
    }

    // =========================================================================
    // STEP 1: Check if we should buffer this patch
    // =========================================================================

    // Case 1: Context is already buffering (previous patch had pending _localUuid)
    if (contextHasPendingLocalUuids(subscriptionId)) {
      bufferDAGPatch(subscriptionId, patch);
      return;
    }

    // Case 2: This patch contains a pending _localUuid - start buffering
    for (const nodeChange of patch.changes.nodes) {
      if (nodeChange.change_type === 'created' || nodeChange.change_type === 'updated') {
        const localUuid = nodeChange.properties?._localUuid;
        if (localUuid && isPendingLocalUuid(localUuid)) {
          markContextAsBuffering(subscriptionId);
          bufferDAGPatch(subscriptionId, patch);
          return;
        }
      }
    }

    // =========================================================================
    // STEP 2: No pending local UUIDs for this context - apply patch directly
    // =========================================================================
    this.applyDAGPatchToGraphStore(patch, subscription);
    this.claimsAfterDraftEdit(subscriptionId, patch, opts);
  }

  /**
   * Apply a DAG patch to GraphStore (extracted for reuse with buffered patches)
   */
  private applyDAGPatchToGraphStore(patch: DAGPatch, subscription: DAGSubscription): void {
    // IMPORTANT: Process ALL nodes FIRST, before any relationships
    // This ensures target nodes exist when relationships reference them

    patch.changes.nodes.forEach(nodeChange => {
      switch (nodeChange.change_type) {
        case 'created':
        case 'updated':
          // Computed values for a record not held yet: kept aside, laid over
          // the record when it lands. Stored as a node of their own they made
          // a half record (no name, no fields) that the record's load then
          // replaced, losing the values.
          if (
            nodeChange.change_type === 'updated' &&
            (patch as { derived?: boolean }).derived === true &&
            !graphStore.getNode(nodeChange.node_id) &&
            !subscription.nodes.get(nodeChange.node_id)
          ) {
            this.holdEarlyEnrichment(subscription.subscription_id, nodeChange.node_id, nodeChange.properties ?? {});
            break;
          }
          // Convert patch node structure to our node format with proper instantiation
          // NO `version` DEFAULT HERE. An op-derived patch never carries one
          // — the ORM's write counter is a framework field and capture
          // excludes it — so defaulting it to 1 and then merging wrote 1 over
          // the stored node's real version on every partial update. The
          // default belongs where there is nothing to keep: see `toLoad`.
          const nodeData: any = {
            id: nodeChange.node_id,
            _label: nodeChange.node_type,
            ...nodeChange.properties
          };

          // Transform data to match schemas before instantiation
          this.transformNodeDataForValidation(nodeData);

          // Instantiate as the appropriate class using cached ModelRegistry (same as sync-dag-response)
          const label = nodeChange.node_type;
          const ModelClass = ModelRegistry[label];
          let nodeInstance: any;

          if (ModelClass && ModelClass.load) {
            // An 'updated' change is a DELTA — the server sends the fields
            // that changed, not the record. Loading one ON ITS OWN builds a
            // whole node with every absent field at its schema DEFAULT, and
            // that node then replaces the one we hold: a silent overwrite of
            // everything the patch did not mention. It stayed hidden because
            // most deltas fail the read schema and take the merge path in the
            // catch below; the ones that happen to satisfy it did not.
            // Measured cost (2026-09-22): a chat's Stop button and
            // "Processing…" never cleared and the input stayed blocked,
            // because a second partial for the same step — carrying `role`
            // and no `is_complete` — reset the completion the first had set.
            // So merge FIRST when we already hold the node, and keep the
            // catch for a delta that cannot pass the schema even merged.
            const stored =
              nodeChange.change_type === 'updated'
                ? graphStore.getNode(nodeChange.node_id) ??
                  subscription.nodes.get(nodeChange.node_id)
                : undefined;
            const toLoad = stored
              ? { ...(stored as any), ...nodeData }
              : { version: 1, ...nodeData };
            try {
              nodeInstance = ModelClass.load(toLoad);
              // Successfully instantiated node from patch
            } catch (error) {
              // A patch is not always a full node: the versioning op lane
              // and streaming deltas send ONLY the changed fields
              // (`{id, version, content}`), which cannot pass the read
              // schema on their own. Overlay them on the node we already
              // hold and re-validate the merged record.
              const existing =
                graphStore.getNode(nodeChange.node_id) ??
                subscription.nodes.get(nodeChange.node_id);
              if (existing) {
                const merged = { ...(existing as any), ...nodeData };
                try {
                  nodeInstance = ModelClass.load(merged);
                } catch (mergeError) {
                  console.error(`❌ Failed to merge ${label} patch onto the stored node:`, mergeError);
                  console.error(`❌ Patch data that failed:`, nodeData);
                  nodeInstance = merged;
                }
              } else {
                console.warn(
                  `⚠️ Partial ${label} patch for a node not in the store yet (held as plain data):`,
                  nodeData,
                );
                // `toLoad`, not `nodeData`: with nothing stored to keep, this
                // carries the version default the record would otherwise lack.
                nodeInstance = toLoad;
              }
            }
          } else {
            console.warn(`⚠️ No ModelClass found for ${label} in patch`);
            // Fallback to plain object if no model class found
            nodeInstance = nodeData;
          }

          // Note: If we reach here, any _localUuid in this node is NOT pending
          // (modify already processed it), so we apply normally
          if (nodeData._localUuid) {
            console.log(`✓ [DAG] Applying (not pending): ${nodeData._localUuid.substring(0, 20)}... → ${nodeChange.node_id.substring(0, 8)}...`);
          }

          subscription.nodes.set(nodeChange.node_id, nodeInstance);

          // Update GraphStore for reactive updates
          // Adding node to GraphStore
          graphStore.updateNode(nodeInstance);
          break;
        case 'deleted':
          subscription.nodes.delete(nodeChange.node_id);

          // Update GraphStore for reactive updates
          graphStore.deleteNode(nodeChange.node_id);
          break;
      }
    });

    // NOW process relationships AFTER all nodes are in place
    patch.changes.relationships.forEach(relChange => {
      // Use same key format as sync-dag-response for consistency
      const relationshipKey = `${relChange.source_id}-${relChange.relationship_type}-${relChange.target_id}`;
      switch (relChange.operation_type) {
        case 'added':
        case 'updated':
          subscription.relationships.set(relationshipKey, {
            id: relationshipKey,
            source: relChange.source_id,    // Normalize to 'source' field
            target: relChange.target_id,    // Normalize to 'target' field
            type: relChange.relationship_type,
            relationship_type: relChange.relationship_type
          } as any);
          this.setEdge(
            subscription.subscription_id, relChange.source_id, relChange.target_id,
            relChange.relationship_type, relChange.properties,
          );
          break;
        case 'removed':
          subscription.relationships.delete(relationshipKey);
          this.removeEdge(
            subscription.subscription_id, relChange.source_id, relChange.target_id, relChange.relationship_type,
            { source: relChange.source_type, target: relChange.target_type },
          );
          break;
      }
    });

    // Update the root node with new DAG version to trigger reactivity
    // This ensures useReactiveNode(rootNodeId) returns a fresh instance
    if (patch.context_id) {
      const rootNode = subscription.nodes.get(patch.context_id);
      if (rootNode) {
        // Update the root node's version to match the DAG patch version
        (rootNode as any).version = patch.version;
        // Trigger atom update in GraphStore to notify subscribers
        graphStore.updateNode(rootNode as any);
      }
    }

    subscription.version = patch.version;
    subscription.timestamp = patch.timestamp;

    // the edit's own computed values, in the same step as its changes: a
    // page never shows the edit without its dependants' stale flags. After
    // the nodes, so a record the edit created is there to take them.
    this.applyFrameComputed(subscription.subscription_id, patch);

    this.notifySubscriptionUpdate(subscription);
  }

  /** An op frame's `computed` items (wire §3.1) through the computed
   *  channel's own path: newer `_v` wins, a record not loaded yet takes them
   *  when it lands. They do not move the channel's cursor: its next `since`
   *  may serve the same items again, which changes nothing. */
  private applyFrameComputed(subscriptionId: string, patch: DAGPatch): void {
    const items = patch.computed;
    if (Array.isArray(items) && items.length) this.applyComputed(subscriptionId, items);
  }


  private notifySubscriptionUpdate(subscription: DAGSubscription): void {
    if (this.serving.has(subscription.subscription_id) && !subscription.isLoading) this.scheduleStore(subscription.subscription_id);
    const handlers = this.eventHandlers.get(subscription.subscription_id);
    if (handlers && handlers.size > 0) {
      // Notify all handlers for this subscription
      handlers.forEach(handler => handler(subscription));
    } else {
      // Buffer the update until handlers are registered
      if (!this.pendingUpdates.has(subscription.subscription_id)) {
        this.pendingUpdates.set(subscription.subscription_id, []);
      }
      this.pendingUpdates.get(subscription.subscription_id)!.push(subscription);
      // Only log if we have more than 2 pending (first message is expected to arrive before handler)
      if (this.pendingUpdates.get(subscription.subscription_id)!.length > 2) {
        console.warn('DAG Client: Buffering update, handler not registered yet:', subscription.subscription_id);
      }
    }
  }

  private notifyErrorHandlers(error: string): void {
    this.errorHandlers.forEach(handler => handler(error));
  }

  // Utility Methods

  private generateSubscriptionId(context: DAGContext): string {
    return `${context.context.label}:${context.context_id}`;
  }
}